import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/SearchFlights.css';
import '../styles/SearchFlightsTrustBar.css';
import '../styles/FlightDetails.css';
import backgroundImage from '../assets/search-hero-bg-12.jpg';
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../context/LoginModalContext';
import FlightResult from '../components/FlightResult';
import FarePickerModal from '../components/FarePickerModal';
import SearchLoadingShell from '../components/SearchLoadingShell';
import { flightsAPI } from '../services/apiClient';
import usePageMeta from '../hooks/usePageMeta';
import CurrencySelector from '../components/CurrencySelector';
import DatePickerInput from '../components/DatePickerInput';
import useExchangeRates from '../hooks/useExchangeRates';
import SearchFormTailwind from '../components/SearchFormTailwind';
import { 
  validatePassengerMix, 
  validateDifferentAirports, 
  validateSearchDateRange,
  validateAirportCode 
} from '../utils/validation';
import {
  DEFAULT_CURRENCY,
  detectUserCurrency,
  convertAmount,
  formatCurrency,
  formatCurrencyParts,
  getSurchargeRate,
} from '../utils/pricing';
import {
  BLOCKED_CARRIER_CODES,
  TIME_FILTER_OPTIONS,
  STOP_FILTER_OPTIONS,
  matchesTimeFilter,
  getStopCountFromFlight,
  isSingleStopSameCarrier,
  parseDurationToMinutes,
  getCarrierCodeFromFlight,
  getFlightAirlineName,
  isBlockedAirlineFlight,
  getAirlineInitials,
  getStopLabel,
  getFlightGroupKey,
  formatShortDate,
  getArrivalDateLabel,
  assignFlightTags,
} from '../utils/flightUtils';
import AirportSearchInput from '../components/AirportSearchInput';


const RESCHEDULE_CONTEXT_KEY = 'airfly_reschedule_context';

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayDateString = () => formatDateForInput(new Date());

const addDaysToDateString = (dateString, days) => {
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return getTodayDateString();
  }

  date.setDate(date.getDate() + days);
  return formatDateForInput(date);
};

const getDefaultSearchDate = () => addDaysToDateString(getTodayDateString(), 1);

const getDayAbbrev = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][d.getDay()];
};

const formatChipDate = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]}`;
};

const classOptions = ['Economy', 'Premium Economy', 'Business', 'First Class'];
const DEFAULT_ROUND_TRIP_OFFSET_DAYS = 7;
const LOADING_SKELETON_COUNT = 3;
const LOADING_SHELL_EXIT_MS = 260;
const SEARCH_RESULTS_STORAGE_KEY = 'airfly_search_results';
const SEARCH_RESULTS_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour - auto-clear stale results to prevent "1453 mins old" bugs
const DOMESTIC_AUSTRALIA_ROUTE_CANDIDATES = [
  {
    id: 'mel-syd',
    from: 'Melbourne',
    to: 'Sydney',
    dayOffset: 8,
    basePrice: 89,
    insight: 'High-frequency corridor with competitive carrier pricing.',
  },
  {
    id: 'syd-bne',
    from: 'Sydney',
    to: 'Brisbane',
    dayOffset: 9,
    basePrice: 95,
    insight: 'Strong weekday seat supply often unlocks value fares.',
  },
  {
    id: 'mel-adl',
    from: 'Melbourne',
    to: 'Adelaide',
    dayOffset: 10,
    basePrice: 79,
    insight: 'Short-haul sector where budget and full-service prices converge.',
  },
  {
    id: 'bne-cns',
    from: 'Brisbane',
    to: 'Cairns',
    dayOffset: 12,
    basePrice: 119,
    insight: 'Next-week inventory often dips before weekend demand peaks.',
  },
  {
    id: 'adl-per',
    from: 'Adelaide',
    to: 'Perth',
    dayOffset: 11,
    basePrice: 139,
    insight: 'Mid-week westbound schedules can open up better value inventory.',
  },
  {
    id: 'syd-hba',
    from: 'Sydney',
    to: 'Hobart',
    dayOffset: 10,
    basePrice: 109,
    insight: 'Shoulder travel dates often produce sharper Tasmania fares.',
  },
  {
    id: 'mel-gld',
    from: 'Melbourne',
    to: 'Gold Coast',
    dayOffset: 9,
    basePrice: 99,
    insight: 'Competition across leisure routes can suppress one-way prices.',
  },
  {
    id: 'per-bne',
    from: 'Perth',
    to: 'Brisbane',
    dayOffset: 12,
    basePrice: 149,
    insight: 'Advance domestic inventory can occasionally undercut usual averages.',
  },
];

const AI_DEALS_MAX_ROUTES = 4;
const AI_DEAL_SELECTION_PROMPT = 'Find the top 4 best-value domestic routes within Australia for next week, prioritizing lowest live one-way Economy fares.';

const SEARCH_RESULTS_RESTORE_ALLOWED_KEY = 'airfly_search_results_restore_allowed';
// Read reschedule context at module-evaluation time so useState init closures can use it
const readAndClearRescheduleContext = (() => {
  let _result = null;
  let _read = false;
  return () => {
    if (!_read) {
      _read = true;
      try {
        const raw = sessionStorage.getItem(RESCHEDULE_CONTEXT_KEY);
        if (raw) {
          _result = JSON.parse(raw);
          sessionStorage.removeItem(RESCHEDULE_CONTEXT_KEY);
        }
      } catch { /* ignore */ }
    }
    return _result;
  };
})();

const getLoadingNarrative = ({
  isReturn,
  origin,
  destination,
  outboundDate,
  returnDate,
  travelClass,
  directOnly,
}) => {
  if (isReturn) {
    return {
      badge: 'Building return plan',
      title: 'Lining up your journey home',
      message: `Comparing ${origin} to ${destination} on ${formatShortDate(returnDate)} to pair the cleanest trip back with your outbound choice.`,
      status: directOnly ? 'Tracing direct options back home' : 'Tracing the smoothest path back home',
      metaRoute: `${origin} to ${destination}`,
      metaDate: formatShortDate(returnDate),
      metaMode: directOnly ? 'Direct return focus' : 'Best return combinations',
    };
  }

  return {
    badge: 'Scanning departure fares',
    title: 'Curating your takeoff options',
    message: `Reviewing ${origin} to ${destination} on ${formatShortDate(outboundDate)} in ${travelClass} for the strongest departure choices.`,
    status: directOnly ? 'Tracking the fastest direct departures' : 'Tracking live fares across airlines',
    metaRoute: `${origin} to ${destination}`,
    metaDate: formatShortDate(outboundDate),
    metaMode: directOnly ? 'Direct flights only' : `${travelClass} cabin`,
  };
};

const getSavedSearchState = () => {
  // Pure read — no side effects. Cleanup is handled in a useEffect after mount.
  const canRestore = sessionStorage.getItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY);
  if (!canRestore) return null;
  try {
    const raw = sessionStorage.getItem(SEARCH_RESULTS_STORAGE_KEY);
    if (!raw) return null;
    
    const parsed = JSON.parse(raw);
    // Check if results are older than 30 minutes
    if (parsed.searchTimestamp) {
      const ageMs = Date.now() - parsed.searchTimestamp;
      if (ageMs > SEARCH_RESULTS_MAX_AGE_MS) {
        // Results expired - clear storage and return null
        sessionStorage.removeItem(SEARCH_RESULTS_STORAGE_KEY);
        return null;
      }
    }
    
    return parsed;
  } catch {
    return null;
  }
};

// Wraps flightsAPI.search with a single silent retry on server-timeout (503 / "temporarily unavailable").
// The loading spinner stays on between attempts so the user never sees a flash of error.
// Increased retry delay to 2s to give cold containers more time to warm up on AWS.
const searchWithAutoRetry = (from, to, date, travelClass, filters) =>
  flightsAPI.search(from, to, date, travelClass, filters).catch((err) => {
    const isTimeout = (err.message || '').toLowerCase().includes('temporarily unavailable');
    if (!isTimeout) throw err;
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        flightsAPI.search(from, to, date, travelClass, filters).then(resolve).catch(reject);
      }, 2000);
    });
  });

function SearchFlights() {
  useEffect(() => {
    document.title = 'Book Flights Faster — AirFlyNow';
    // Warmup backend on page load to reduce cold-start latency (production only)
    flightsAPI.warmup();
  }, []);
  usePageMeta({
    title: 'Book Flights Faster | AirFlyNow — Smart Flight Tagging',
    description: 'Instantly identify Cheapest, Fastest, Best Value. Search hundreds of airlines at a glance. All cabin classes available.',
  });
  const navigate = useNavigate();
  const location = useLocation();
  
  // Authentication hooks
  const { isAuthenticated = false } = useAuth() || {};
  const { openLoginModal = () => {} } = useLoginModal() || {};
  
  const travellerPanelRef = useRef(null);
  const returnDatePickerRef = useRef(null);
  
  // Track scroll position to restore after search results load
  const scrollPositionToRestore = useRef(null);
  // Track if we're restoring from cache (vs new search) to skip loading shell
  const isRestoringFromCache = useRef(false);

  // Params passed from BookingPreview "Refresh results" or FlightDetails "Back to results"
  // via React Router location state — fresh per navigation, consumed once then cleared.
  const autoSearchParamsRef = useRef(
    location.state?.autoSearch ? location.state : null
  );
  const autoSearchParams = autoSearchParamsRef.current;
  const resultsRef = useRef(null);
  // Holds the in-flight Promise for the return leg pre-fetch so handleSelectFlight
  // can await it instantly rather than kicking off a new Duffel round-trip.
  const returnPrefetchRef = useRef(null);
  const todayDate = getTodayDateString();

  // Read and consume reschedule context once on mount
  const [rescheduleContext] = useState(() => readAndClearRescheduleContext());

  // Read saved search state exactly once per mount (plain call — safe because it's in a useState
  // initializer which only runs once). autoSearch overrides any saved state.
  const [savedSearchState] = useState(() => autoSearchParams ? null : getSavedSearchState());

  const [formData, setFormData] = useState(() => {
    // Auto-search: pre-fill from the expired offer's original search context
    if (autoSearchParams) {
      const depDate = autoSearchParams.date || getDefaultSearchDate();
      return {
        from: autoSearchParams.from || 'Melbourne',
        to: autoSearchParams.to || 'New Delhi',
        date: depDate,
        returnDate: autoSearchParams.returnDate || addDaysToDateString(depDate, DEFAULT_ROUND_TRIP_OFFSET_DAYS),
        class: autoSearchParams.travelClass || 'Economy',
        adults: autoSearchParams.adults ?? 1,
        children: autoSearchParams.children ?? 0,
        infants: autoSearchParams.infants ?? 0,
      };
    }
    const ctx = readAndClearRescheduleContext();
    // In reschedule mode always start fresh — never restore stale search state
    if (ctx?.lockRoute) {
      const depDate = getDefaultSearchDate();
      return {
        from: ctx.from || 'Melbourne',
        to: ctx.to || 'New Delhi',
        date: depDate,
        returnDate: '',  // Empty for one-way reschedule
        class: 'Economy',
        adults: 1,
        children: 0,
        infants: 0,
      };
    }
    const saved = savedSearchState?.formData;
    if (saved) return saved;
    const depDate = getDefaultSearchDate();
    return {
      from: ctx?.from || 'Melbourne',
      to: ctx?.to || 'New Delhi',
      date: depDate,
      returnDate: '',  // Empty for default one-way
      class: 'Economy',
      adults: 1,
      children: 0,
      infants: 0,
    };
  });

  const [tripType, setTripType] = useState(() => {
    if (autoSearchParams) return autoSearchParams.tripType || 'one-way';
    // Reschedule is always one-way on the same leg — ignore saved state
    if (readAndClearRescheduleContext()?.lockRoute) return 'one-way';
    // Always default to one-way for new searches
    return 'one-way';
  });
  const [travellerPanelOpen, setTravellerPanelOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [filters, setFilters] = useState(() => savedSearchState?.filters ?? {
    maxPrice: 2200,
    sortBy: 'price',
    airlines: [],
    stops: [],
    departureWindow: 'any',
    arrivalWindow: 'any',
  });

  // Restore full flight arrays from sessionStorage for instant back-button UX
  // But clear them if they're older than 6 hours (cache TTL)
  const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours - must match apiClient.js
  const [flights, setFlights] = useState(() => {
    const timestamp = savedSearchState?.searchTimestamp;
    if (timestamp && Date.now() - timestamp > CACHE_TTL_MS) {
      // Results expired - clear them
      return [];
    }
    return savedSearchState?.flights ?? [];
  });
  const [returnFlights, setReturnFlights] = useState(() => {
    const timestamp = savedSearchState?.searchTimestamp;
    if (timestamp && Date.now() - timestamp > CACHE_TTL_MS) return [];
    return savedSearchState?.returnFlights ?? [];
  });
  const [selectedOutboundFlight, setSelectedOutboundFlight] = useState(() => {
    const timestamp = savedSearchState?.searchTimestamp;
    if (timestamp && Date.now() - timestamp > CACHE_TTL_MS) return null;
    return savedSearchState?.selectedOutboundFlight ?? null;
  });
  const [selectedReturnFlight, setSelectedReturnFlight] = useState(() => {
    const timestamp = savedSearchState?.searchTimestamp;
    if (timestamp && Date.now() - timestamp > CACHE_TTL_MS) return null;
    return savedSearchState?.selectedReturnFlight ?? null;
  });
  const [searchTimestamp, setSearchTimestamp] = useState(() => {
    const timestamp = savedSearchState?.searchTimestamp;
    if (timestamp && Date.now() - timestamp > CACHE_TTL_MS) {
      // Timestamp expired - clear it
      return null;
    }
    return timestamp;
  });
  
  // Store raw combined offers for finding matching returns by flight characteristics
  // This allows showing ALL return options for flights with same characteristics
  const [combinedOffers, setCombinedOffers] = useState([]);
  const [mixedAirlineOffersInfo, setMixedAirlineOffersInfo] = useState(null);
  
  const [returnLoading, setReturnLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingShellVisible, setLoadingShellVisible] = useState(false);
  const [loadingShellExiting, setLoadingShellExiting] = useState(false);
  const [error, setError] = useState('');
  const [returnError, setReturnError] = useState('');
  const [, setStaleCacheNotice] = useState(null); // timestamp when stale cached results are shown
  const [hasSearched, setHasSearched] = useState(() => savedSearchState?.hasSearched ?? false);
  const [directOnlySearch, setDirectOnlySearch] = useState(() => savedSearchState?.directOnlySearch ?? false);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    // Priority: 1. Saved search state, 2. Auto-detected from timezone, 3. Default AUD
    if (savedSearchState?.selectedCurrency) {
      return savedSearchState.selectedCurrency;
    }
    return detectUserCurrency();
  });
  const [farePickerGroup, setFarePickerGroup] = useState(null); // Flight[] | null
  const [quickSortPreset, setQuickSortPreset] = useState(() => savedSearchState?.quickSortPreset ?? 'cheapest');
  const [otherSortBy, setOtherSortBy] = useState('price');
  const [surchargeRate, setSurchargeRate] = useState(0.02);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [staleSearchModal, setStaleSearchModal] = useState(false);
  
  // Domestic flights service notice
  const [showDomesticNotice, setShowDomesticNotice] = useState(() => {
    return localStorage.getItem('airfly_domestic_notice_dismissed') !== 'true';
  });
  
  const dismissDomesticNotice = () => {
    setShowDomesticNotice(false);
    localStorage.setItem('airfly_domestic_notice_dismissed', 'true');
  };
  
  const [dateWindowStart, setDateWindowStart] = useState(null);
  const [returnDateWindowStart, setReturnDateWindowStart] = useState(null);
  const [collapsedFilters, setCollapsedFilters] = useState({
    stops: false,
    price: false,
    time: false,
    arrival: false,
    airlines: false,
  });
  const [aiDealPrices, setAiDealPrices] = useState(() => (
    Object.fromEntries(
      DOMESTIC_AUSTRALIA_ROUTE_CANDIDATES.map((deal) => [deal.id, {
        loading: false,
        price: null,
        airline: null,
      }]),
    )
  ));
  const [aiSelectedDealIds, setAiSelectedDealIds] = useState(() => (
    DOMESTIC_AUSTRALIA_ROUTE_CANDIDATES.slice(0, AI_DEALS_MAX_ROUTES).map((deal) => deal.id)
  ));
  const [showRoundTripBar, setShowRoundTripBar] = useState(true);
  const { exchangeRates } = useExchangeRates('AUD');

  const minimumReturnDate = formData.date; // same day return allowed
  const totalTravellers = formData.adults + formData.children + formData.infants;
  const travellerSummary = `${totalTravellers} Traveller${totalTravellers > 1 ? 's' : ''}, ${formData.class}`;
  const isRoundTrip = tripType === 'round-trip';
  const isSelectingReturn = isRoundTrip && Boolean(selectedOutboundFlight);
  
  // Allow users to select any outbound + any return flight
  // Backend will find combined offer if available, otherwise creates two separate bookings
  const activeFlights = isSelectingReturn ? returnFlights : flights;

  useEffect(() => {
    const shouldResetSearch = Boolean(location.state?.resetSearch);
    if (!shouldResetSearch) return;

    const defaultFrom = rescheduleContext?.from || 'Melbourne';
    const defaultTo = rescheduleContext?.to || 'New Delhi';
    const depDate = getDefaultSearchDate();

    sessionStorage.removeItem(SEARCH_RESULTS_STORAGE_KEY);
    sessionStorage.removeItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY);

    setFormData({
      from: defaultFrom,
      to: defaultTo,
      date: depDate,
      returnDate: '',  // Empty for default one-way
      class: 'Economy',
      adults: 1,
      children: 0,
      infants: 0,
    });
    setTripType('one-way');
    setTravellerPanelOpen(false);
    setFieldErrors({});
    setFilters({
      maxPrice: 2200,
      sortBy: 'price',
      airlines: [],
      stops: [],
      departureWindow: 'any',
      arrivalWindow: 'any',
    });
    setFlights([]);
    setReturnFlights([]);
    setSelectedOutboundFlight(null);
    setSelectedReturnFlight(null);
    setSearchTimestamp(null);
    setReturnLoading(false);
    setLoading(false);
    setLoadingShellVisible(false);
    setLoadingShellExiting(false);
    setError('');
    setReturnError('');
    setHasSearched(false);
    setDirectOnlySearch(false);
    setStaleSearchModal(false);
    setQuickSortPreset('cheapest');
    setOtherSortBy('price');
    setFilterDrawerOpen(false);
    setDateWindowStart(null);
    setReturnDateWindowStart(null);
    setCollapsedFilters({
      stops: false,
      price: false,
      time: false,
      arrival: false,
      airlines: false,
    });
    setFarePickerGroup(null);
    setShowRoundTripBar(true);
    returnPrefetchRef.current = null;

    // Consume the reset flag so refresh/back doesn't keep resetting.
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, rescheduleContext]);

  // Handle back button: clear search results and show form again
  useEffect(() => {
    const handlePopState = () => {
      // If we have search results and user presses back, clear results
      if (hasSearched && flights.length > 0) {
        setFlights([]);
        setReturnFlights([]);
        setHasSearched(false);
        setSelectedOutboundFlight(null);
        setSelectedReturnFlight(null);
        setError('');
        setReturnError('');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasSearched, flights.length]);

  // Show stale search modal when results are 10+ minutes old
  // Auto-clear results if they're older than 60 minutes (1 hour)
  useEffect(() => {
    if (!hasSearched || !searchTimestamp || staleSearchModal) return;

    const checkAge = () => {
      const ageMinutes = Math.floor((Date.now() - searchTimestamp) / 60000);
      
      // Auto-clear extremely stale results (60+ minutes = 1 hour)
      if (ageMinutes >= 60) {
        setFlights([]);
        setReturnFlights([]);
        setSelectedOutboundFlight(null);
        setSelectedReturnFlight(null);
        setSearchTimestamp(null);
        setHasSearched(false);
        setStaleSearchModal(false);
        return;
      }
      
      if (ageMinutes >= 10) {
        setStaleSearchModal(true);
      }
    };

    // Check immediately
    checkAge();

    // Check every minute
    const interval = setInterval(checkAge, 60000);
    return () => clearInterval(interval);
  }, [hasSearched, searchTimestamp, staleSearchModal]);

  const visibleActiveFlights = useMemo(() => {
    const filtered = activeFlights.filter((flight) => {
      if (isBlockedAirlineFlight(flight)) {
        return false;
      }

      const carrierCode = getCarrierCodeFromFlight(flight);
      const normalizedAirlineCode = String(flight?.airline || '').trim().toUpperCase();
      if (
        (carrierCode && BLOCKED_CARRIER_CODES.has(carrierCode))
        || BLOCKED_CARRIER_CODES.has(normalizedAirlineCode)
      ) {
        return false;
      }

      const stopCount = getStopCountFromFlight(flight);
      // Round-trips: Allow up to 2 stops (e.g., MEL→SIN→DEL)
      // One-way: Strict 1 stop limit for quality
      if (isRoundTrip && stopCount > 2) {
        return false;
      } else if (!isRoundTrip && stopCount > 1) {
        return false;
      }

      // In reschedule mode: only show flights operated by the original booking's airline.
      // Codeshare 1-stop flights (mixed segment carriers) are allowed because the marketing
      // carrier on the first segment still matches — this is the normal case for international routes.
      if (rescheduleContext?.lockRoute && rescheduleContext?.carrierCode) {
        const flightCarrier = getCarrierCodeFromFlight(flight);
        const originalCarrier = rescheduleContext.carrierCode.toUpperCase();
        // Allow if any segment matches the original carrier (covers codeshare itineraries)
        const segmentCarriers = Array.isArray(flight?.segments)
          ? flight.segments.map((s) => String(s?.carrierCode || '').toUpperCase()).filter(Boolean)
          : flightCarrier ? [flightCarrier] : [];
        if (segmentCarriers.length > 0 && !segmentCarriers.includes(originalCarrier)) {
          return false;
        }
        return true;
      }

      // For round-trips: Allow ALL airlines including codeshare flights (mixed carriers in segments)
      // This enables mixed-airline round-trip offers (e.g., SriLankan outbound + Air India return)
      // Only restriction: max 1 stop per flight (handled above)
      if (isRoundTrip) {
        return true;
      }

      // For one-way: Apply stricter same-carrier filtering for quality
      return isSingleStopSameCarrier(flight);
    });
    
    return filtered;
  }, [activeFlights, rescheduleContext, isRoundTrip, isSelectingReturn]);

  const stopFilterStats = useMemo(() => {
    const stats = {
      nonstop: { count: 0, minPrice: null },
      onestop: { count: 0, minPrice: null },
    };
    const seenGroupKeys = new Set();

    visibleActiveFlights.forEach((flight) => {
      const stopCount = getStopCountFromFlight(flight);
      const key = stopCount === 0 ? 'nonstop' : stopCount === 1 ? 'onestop' : null;
      if (!key) {
        return;
      }

      // Always track cheapest price regardless of fare duplicates
      if (stats[key].minPrice == null || flight.price < stats[key].minPrice) {
        stats[key].minPrice = flight.price;
      }

      // Only count each unique physical flight once (skip fare variants)
      const groupKey = getFlightGroupKey(flight);
      if (seenGroupKeys.has(groupKey)) return;
      seenGroupKeys.add(groupKey);
      stats[key].count += 1;
    });

    return stats;
  }, [visibleActiveFlights]);
  
  // Show loading indicator for both initial search AND return flight search (sequential flow)
  const isLoadingResults = loading || (isSelectingReturn && returnLoading);
  
  // Hide loading shell when restoring from cache for instant UX
  const shouldShowLoadingShell = !isRestoringFromCache.current && (isLoadingResults || loadingShellVisible);

  // Slider ceiling: max(result prices) * 1.1, rounded up to nearest $50, min $1000.
  // Recomputed after every search so the range always spans the actual result set.
  const priceSliderMax = useMemo(() => {
    if (!flights.length) return 3000;
    const maxRaw = Math.max(...flights.map((f) => Number(f.price) || 0));
    if (maxRaw <= 0) return 3000;
    return Math.max(Math.ceil((maxRaw * 1.1) / 50) * 50, 1000);
  }, [flights]);

  // Keep the max-price filter in sync with the result set ceiling after each search.
  useEffect(() => {
    setFilters((prev) => ({ ...prev, maxPrice: priceSliderMax }));
  }, [priceSliderMax]);

  const quickOptionInsights = useMemo(() => {
    if (visibleActiveFlights.length === 0) {
      return {
        cheapest: null,
        nonstop: null,
        preferred: null,
      };
    }

    const cheapest = [...visibleActiveFlights].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0] || null;

    const nonstop = [...visibleActiveFlights]
      .filter((flight) => getStopCountFromFlight(flight) === 0)
      .sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0] || null;

    const preferred = [...visibleActiveFlights]
      .sort((a, b) => {
        const stopPenaltyA = getStopCountFromFlight(a) * 180;
        const stopPenaltyB = getStopCountFromFlight(b) * 180;
        const scoreA = Number(a.price || 0) + stopPenaltyA + (parseDurationToMinutes(a.duration) / 4);
        const scoreB = Number(b.price || 0) + stopPenaltyB + (parseDurationToMinutes(b.duration) / 4);
        return scoreA - scoreB;
      })[0] || null;

    const fastest = [...visibleActiveFlights]
      .sort((a, b) => parseDurationToMinutes(a.duration) - parseDurationToMinutes(b.duration))[0] || null;

    return { cheapest, nonstop, preferred, fastest };
  }, [visibleActiveFlights]);

  const airlineFilterOptions = useMemo(() => {
    const indexByName = new Map();
    const seenGroupKeys = new Set();

    visibleActiveFlights.forEach((flight) => {
      const name = getFlightAirlineName(flight);
      if (!name) {
        return;
      }

      const carrierCode = getCarrierCodeFromFlight(flight);
      const groupKey = getFlightGroupKey(flight);
      const logoUrl = flight.logoUrl || (flight.segments && flight.segments[0]?.logoUrl) || null;
      const current = indexByName.get(name);
      if (!current) {
        indexByName.set(name, {
          name,
          count: 1,
          carrierCodes: carrierCode ? new Set([carrierCode]) : new Set(),
          seenKeys: new Set([groupKey]),
          logoUrl: logoUrl, // Store Duffel logo URL
        });
        return;
      }

      if (carrierCode) {
        current.carrierCodes.add(carrierCode);
      }
      // Store first non-null logo URL we encounter
      if (!current.logoUrl && logoUrl) {
        current.logoUrl = logoUrl;
      }
      // Only increment count for unique physical flights
      if (!current.seenKeys.has(groupKey)) {
        current.seenKeys.add(groupKey);
        current.count += 1;
      }
      // Also guard the outer seenGroupKeys to match stopFilterStats behaviour
      seenGroupKeys.add(groupKey);
    });

    return Array.from(indexByName.values())
      .map((entry) => ({
        name: entry.name,
        count: entry.count,
        logoCode: Array.from(entry.carrierCodes).sort()[0] || '', // Keep for fallback
        logoUrl: entry.logoUrl, // Add Duffel logo URL
      }))
      .sort((airlineA, airlineB) => airlineA.name.localeCompare(airlineB.name));
  }, [visibleActiveFlights]);

  const availableAirlineNames = useMemo(
    () => airlineFilterOptions.map((option) => option.name),
    [airlineFilterOptions],
  );

  const aiRankedDomesticDeals = useMemo(() => {
    const candidateById = new Map(DOMESTIC_AUSTRALIA_ROUTE_CANDIDATES.map((deal) => [deal.id, deal]));

    const aiOrderedDeals = aiSelectedDealIds
      .map((dealId) => candidateById.get(dealId))
      .filter(Boolean);

    const aiChosenIds = new Set(aiOrderedDeals.map((deal) => deal.id));
    const remainingDeals = DOMESTIC_AUSTRALIA_ROUTE_CANDIDATES
      .filter((deal) => !aiChosenIds.has(deal.id))
      .sort((dealA, dealB) => Number(dealA.basePrice) - Number(dealB.basePrice));

    const prioritizedDeals = [...aiOrderedDeals, ...remainingDeals].slice(0, AI_DEALS_MAX_ROUTES);

    const mergedDeals = prioritizedDeals.map((deal) => {
      const liveInfo = aiDealPrices[deal.id] || { loading: true, price: null, airline: null };
      const hasLiveFare = liveInfo.price != null;

      return {
        ...deal,
        loading: Boolean(liveInfo.loading),
        hasLiveFare,
        displayPrice: hasLiveFare ? liveInfo.price : deal.basePrice,
        airline: liveInfo.airline,
      };
    });

    return mergedDeals;
  }, [aiDealPrices, aiSelectedDealIds]);

  useEffect(() => {
    let disposed = false;

    Promise.resolve(getSurchargeRate())
      .then((rate) => {
        if (!disposed) {
          setSurchargeRate(rate);
        }
      })
      .catch(() => {
        if (!disposed) {
          setSurchargeRate(0.02);
        }
      });

    return () => {
      disposed = true;
    };
  }, []);

  const formatDisplayPrice = (amount, currency = 'AUD') => formatCurrency(
    convertAmount(amount, currency, selectedCurrency, exchangeRates) * (1 + surchargeRate),
    selectedCurrency,
  );

  const getDisplayPriceAmount = (amount, currency = 'AUD') => convertAmount(
    amount,
    currency,
    selectedCurrency,
    exchangeRates,
  ) * (1 + surchargeRate);

  // NEW: Format combined offer prices WITH surcharge (apply 2% markup like regular flights)
  const formatCombinedOfferPrice = (amount, currency = 'AUD') => formatCurrency(
    convertAmount(amount, currency, selectedCurrency, exchangeRates) * (1 + surchargeRate),
    selectedCurrency,
  );

  const getCombinedOfferPriceAmount = (amount, currency = 'AUD') => convertAmount(
    amount,
    currency,
    selectedCurrency,
    exchangeRates,
  ) * (1 + surchargeRate);

  useEffect(() => {
    if (!travellerPanelOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (travellerPanelRef.current && !travellerPanelRef.current.contains(event.target)) {
        setTravellerPanelOpen(false);
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    return () => window.removeEventListener('mousedown', handlePointerDown);
  }, [travellerPanelOpen]);

  // Focus trap: keyboard navigation within the traveller panel when open
  useEffect(() => {
    if (!travellerPanelOpen) {
      return undefined;
    }

    const panel = travellerPanelRef.current?.querySelector('.traveller-panel');
    if (!panel) {
      return undefined;
    }

    const getFocusable = () => Array.from(
      panel.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'),
    );

    // Move focus into the panel on open
    const firstFocusable = getFocusable()[0];
    if (firstFocusable) {
      firstFocusable.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setTravellerPanelOpen(false);
        travellerPanelRef.current?.querySelector('.traveller-summary-btn')?.focus();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const focusable = getFocusable();
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', handleKeyDown);
    return () => panel.removeEventListener('keydown', handleKeyDown);
  }, [travellerPanelOpen]);

  useEffect(() => {
    let isMounted = true;

    Promise.resolve(flightsAPI.getLatestAiDomesticDeals())
      .then((snapshot) => {
        if (!isMounted) {
          return;
        }

        const snapshotDeals = Array.isArray(snapshot?.deals)
          ? snapshot.deals.filter((deal) => deal && deal.id)
          : [];

        if (snapshotDeals.length > 0) {
          const snapshotIds = snapshotDeals
            .map((deal) => deal.id)
            .slice(0, AI_DEALS_MAX_ROUTES);

          const priceMap = {};
          snapshotDeals.forEach((deal) => {
            priceMap[deal.id] = {
              loading: false,
              price: Number.isFinite(Number(deal.price)) ? Number(deal.price) : null,
              airline: deal.airline || null,
            };
          });

          setAiSelectedDealIds(snapshotIds);
          setAiDealPrices((prev) => ({
            ...prev,
            ...priceMap,
          }));
        }
      })
      .catch(() => {
        // Silently ignore - backend may not be running or deals unavailable
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isLoadingResults) {
      setLoadingShellVisible(true);
      setLoadingShellExiting(false);
      // Scroll results into view when a new search kicks off (skip during cache restoration)
      if (!isRestoringFromCache.current) {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return undefined;
    }

    if (!loadingShellVisible) {
      return undefined;
    }

    setLoadingShellExiting(true);
    const timerId = window.setTimeout(() => {
      setLoadingShellVisible(false);
      setLoadingShellExiting(false);
    }, LOADING_SHELL_EXIT_MS);

    return () => window.clearTimeout(timerId);
  }, [isLoadingResults, loadingShellVisible]);

  // Lock body scroll while search results are loading (skip during cache restoration)
  useEffect(() => {
    if (isLoadingResults && !isRestoringFromCache.current) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isLoadingResults]);

  // Show round-trip sticky bar always when selecting return flights
  useEffect(() => {
    if (!isSelectingReturn) {
      setShowRoundTripBar(true);
      return undefined;
    }

    // Always show the sticky bar when selecting return flights
    setShowRoundTripBar(true);

    // No scroll listener needed - bar is always visible
    return undefined;
  }, [isSelectingReturn]);

  // Auto-scroll to flights when return flight selection starts (especially important on mobile)
  useEffect(() => {
    if (isSelectingReturn && resultsRef.current) {
      // Small delay to ensure DOM is updated with return flights
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isSelectingReturn]);

  useEffect(() => {
    setFilters((prev) => {
      if (!prev.airlines.length) {
        return prev;
      }

      const nextAirlines = prev.airlines.filter((airline) => availableAirlineNames.includes(airline));
      if (nextAirlines.length === prev.airlines.length) {
        return prev;
      }

      return {
        ...prev,
        airlines: nextAirlines,
      };
    });
  }, [availableAirlineNames]);

  // AUTO-UPDATE RETURN DATE: When departure changes and return becomes invalid
  // This ensures return date stays 7 days ahead when user changes departure
  useEffect(() => {
    if (tripType !== 'round-trip' || !formData.date) {
      return;
    }

    // If return date is empty or strictly before departure, reset to 7 days after departure
    if (!formData.returnDate || formData.returnDate < formData.date) {
      const newReturnDate = addDaysToDateString(formData.date, DEFAULT_ROUND_TRIP_OFFSET_DAYS);
      setFormData(prev => ({
        ...prev,
        returnDate: newReturnDate,
      }));
    }
  }, [formData.date, formData.returnDate, tripType]); // Watch all date changes

  const getDateValidationErrors = (nextFormData = formData, nextTripType = tripType) => {
    const nextErrors = {};

    if (!nextFormData.date) {
      nextErrors.date = 'Departure date is required.';
    } else if (nextFormData.date < todayDate) {
      nextErrors.date = 'Departure date cannot be earlier than today.';
    }

    if (nextTripType === 'round-trip') {
      if (!nextFormData.returnDate) {
        nextErrors.returnDate = 'Return date is required for round trip.';
      } else if (nextFormData.returnDate < nextFormData.date) {
        nextErrors.returnDate = 'Return date cannot be before departure date.';
      }
    }

    return nextErrors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (['from', 'to', 'date', 'returnDate'].includes(name)) {
      setSelectedOutboundFlight(null);
      setSelectedReturnFlight(null);
      setReturnFlights([]);
    }

    setFormData(prev => {
      const nextFormData = {
        ...prev,
        [name]: value
      };

      // AUTO-UPDATE RETURN DATE: When departure changes for round-trip
      if (name === 'date' && tripType === 'round-trip') {
        const nextMinimumReturnDate = value; // same-day return allowed
        // Only reset return date if it now falls BEFORE the new departure (not equal — same day OK)
        if (!nextFormData.returnDate || nextFormData.returnDate < nextMinimumReturnDate) {
          nextFormData.returnDate = addDaysToDateString(value, DEFAULT_ROUND_TRIP_OFFSET_DAYS);
        }
        
        // AUTO-OPEN RETURN DATE PICKER: After selecting departure date for round-trip
        setTimeout(() => {
          if (returnDatePickerRef.current?.openCalendar) {
            returnDatePickerRef.current.openCalendar();
          }
        }, 100);
      } 
      // PREVENT INVALID RETURN DATE: Clear return if it becomes before departure for one-way
      else if (name === 'date' && nextFormData.returnDate && nextFormData.returnDate < value) {
        nextFormData.returnDate = '';
      }

      return nextFormData;
    });

    if (name === 'date' || name === 'returnDate') {
      const nextFormData = {
        ...formData,
        [name]: value,
      };

      // Duplicate check for validation errors (runs after state update)
      if (name === 'date' && tripType === 'round-trip') {
        if (!nextFormData.returnDate || nextFormData.returnDate < value) {
          nextFormData.returnDate = addDaysToDateString(value, DEFAULT_ROUND_TRIP_OFFSET_DAYS);
        }
      } else if (name === 'date' && nextFormData.returnDate && nextFormData.returnDate < value) {
        nextFormData.returnDate = '';
      }

      setFieldErrors(getDateValidationErrors(nextFormData));
    }
  };

  const handleTripTypeChange = (nextTripType) => {
    // Route is locked in reschedule mode — only one-way is allowed
    if (rescheduleContext?.lockRoute) return;
    setTripType(nextTripType);
    setError('');
    setSelectedOutboundFlight(null);
    setSelectedReturnFlight(null);
    setReturnFlights([]);

    if (nextTripType === 'one-way') {
      setFormData(prev => ({
        ...prev,
        returnDate: ''
      }));
      setFieldErrors(getDateValidationErrors({ ...formData, returnDate: '' }, nextTripType));
      return;
    }

    const nextReturnDate = formData.returnDate && formData.returnDate >= formData.date
      ? formData.returnDate
      : addDaysToDateString(formData.date, DEFAULT_ROUND_TRIP_OFFSET_DAYS);

    const nextFormData = {
      ...formData,
      returnDate: nextReturnDate,
    };

    setFormData(nextFormData);
    setFieldErrors(getDateValidationErrors(nextFormData, nextTripType));
  };

  const handleSwap = () => {
    setSelectedOutboundFlight(null);
    setSelectedReturnFlight(null);
    setReturnFlights([]);
    setFormData(prev => ({ ...prev, from: prev.to, to: prev.from }));
  };

  const handlePassengerCountChange = (category, delta) => {
    setFormData(prev => {
      const nextFormData = {
        ...prev,
        [category]: Math.max(category === 'adults' ? 1 : 0, Math.min(9, prev[category] + delta)),
      };

      const nextTotal = nextFormData.adults + nextFormData.children + nextFormData.infants;
      if (nextTotal > 9) {
        return prev;
      }

      return {
        ...nextFormData,
      };
    });
  };

  const handleClassChange = (value) => {
    setSelectedOutboundFlight(null);
    setSelectedReturnFlight(null);
    setReturnFlights([]);
    setFormData(prev => ({
      ...prev,
      class: value,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAirlineFilterToggle = (airlineName) => {
    setFilters((prev) => {
      const isSelected = prev.airlines.includes(airlineName);
      return {
        ...prev,
        airlines: isSelected
          ? prev.airlines.filter((airline) => airline !== airlineName)
          : [...prev.airlines, airlineName],
      };
    });
  };

  const handleStopFilterToggle = (stopKey) => {
    setFilters((prev) => {
      const isSelected = prev.stops.includes(stopKey);
      return {
        ...prev,
        stops: isSelected
          ? prev.stops.filter((key) => key !== stopKey)
          : [...prev.stops, stopKey],
      };
    });
  };

  const handleTimeWindowSelect = (type, windowKey) => {
    setFilters((prev) => {
      const fieldName = type === 'departure' ? 'departureWindow' : 'arrivalWindow';
      const nextValue = prev[fieldName] === windowKey ? 'any' : windowKey;
      return {
        ...prev,
        [fieldName]: nextValue,
      };
    });
  };

  const handleFilterSectionToggle = (sectionKey) => {
    setCollapsedFilters((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const applyQuickPreset = (presetKey) => {
    // Toggle behavior: if already active, clear it; otherwise apply it
    const isAlreadyActive = quickSortPreset === presetKey;

    if (isAlreadyActive) {
      // Toggle OFF: clear to default state
      setQuickSortPreset('other');
      setFilters((prev) => ({
        ...prev,
        sortBy: 'departure',
        stops: [],
      }));
      return;
    }

    // Toggle ON: apply the preset
    setQuickSortPreset(presetKey);

    if (presetKey === 'cheapest') {
      setFilters((prev) => ({
        ...prev,
        sortBy: 'price',
      }));
      return;
    }

    if (presetKey === 'nonstop') {
      setFilters((prev) => ({
        ...prev,
        sortBy: 'price',
        stops: ['nonstop'],
      }));
      return;
    }

    if (presetKey === 'preferred') {
      setFilters((prev) => ({
        ...prev,
        sortBy: 'time',
        stops: [],
      }));
      return;
    }

    setFilters((prev) => ({
      ...prev,
      sortBy: otherSortBy,
    }));
  };

  // Helper function to check if two flights match by characteristics
  // Used to find all return options for a selected outbound flight
  // Matches on carrier+flightNumber+time+cabin, NOT on offer-specific IDs
  const flightsMatchCharacteristics = (flight1, flight2) => {
    if (!flight1 || !flight2) return false;
    
    // Extract carrier + flight number from code (remove offer ID suffix)
    // Format: "SQ-0248-off_0000B5cQhgIhnqvEPN18Nv" → "SQ-0248"
    // This allows matching the SAME physical flight across different combined offers
    const extractFlightCode = (fullCode) => {
      const parts = (fullCode || '').split('-off_');
      return parts[0]; // Returns "SQ-0248" without the offer ID
    };
    
    const code1 = extractFlightCode(flight1.Code || flight1.code).trim();
    const code2 = extractFlightCode(flight2.Code || flight2.code).trim();
    const dept1 = (flight1.DepartureTime || flight1.departureTime || '').trim();
    const dept2 = (flight2.DepartureTime || flight2.departureTime || '').trim();
    const arr1 = (flight1.ArrivalTime || flight1.arrivalTime || '').trim();
    const arr2 = (flight2.ArrivalTime || flight2.arrivalTime || '').trim();
    const class1 = (flight1.Class || flight1.class || '').trim().toLowerCase();
    const class2 = (flight2.Class || flight2.class || '').trim().toLowerCase();
    
    return code1 === code2 
      && dept1 === dept2 
      && arr1 === arr2 
      && class1 === class2;
  };

  const runSearchWithFormData = (nextFormData, options = {}) => {
    const directOnly = options.directOnly ?? false;
    const forceRefresh = options.forceRefresh ?? false;

    sessionStorage.removeItem(SEARCH_RESULTS_STORAGE_KEY);
    setError('');
    setReturnError('');
    setStaleSearchModal(false); // Reset stale search modal on new search
    setSelectedOutboundFlight(null);
    setSelectedReturnFlight(null);
    setReturnFlights([]);
    setHasSearched(true);
    setQuickSortPreset('other');
    setOtherSortBy('departure');
    setFilters((prev) => ({
      ...prev,
      sortBy: 'price',
      stops: [],
      airlines: [],
      departureWindow: 'any',
      arrivalWindow: 'any',
    }));
    setLoading(true);
    setDateWindowStart(null);
    setReturnDateWindowStart(null);
    // Discard any stale pre-fetch — a new outbound search invalidates it.
    returnPrefetchRef.current = null;

    // Push a new history entry when search is performed so back button returns to search form
    // This prevents going back to previous page (e.g., /air-weekly) after searching
    if (!options.skipHistoryPush) {
      navigate(location.pathname, {
        state: { searchPerformed: true, timestamp: Date.now() },
      });
    }

    const searchFilters = directOnlySearch ? { type: 'Direct' } : {};
    if (forceRefresh) {
      searchFilters.forceRefresh = true;
    }

    // ROUND-TRIP SEARCH WITH COMBINED OFFERS: Fetch once, filter on selection
    // Step 1: Load combined offers (includes both outbound and return flights paired)
    // Step 2: Extract unique outbound flights and display
    // Step 3: When user selects outbound → Filter stored combined offers to show matching returns (instant, no API call)
    // Step 4: At payment → Use combinedOfferId from selected offer
    if (nextFormData.returnDate && tripType === 'round-trip') {
      // Call Duffel combined offer API (already cached by backend for 30 min)
      flightsAPI.searchRoundTrip(
        nextFormData.from,
        nextFormData.to,
        nextFormData.date,
        nextFormData.returnDate,
        nextFormData.class,
        searchFilters
      )
        .then((roundTripData) => {
          const timestamp = Date.now();
          
          // Extract combined offers from response
          const offers = roundTripData?.Offers || roundTripData?.offers || [];
          
          if (offers.length === 0) {
            setError('We\'re working to bring flights for this route into our system. Try different dates or airports, or check back soon!');
            setFlights([]);
            setReturnFlights([]);
            setCombinedOffers([]);
            setMixedAirlineOffersInfo(null);
            setSearchTimestamp(null);
            setLoading(false);
            return;
          }

          // Store combined offers for later filtering when outbound is selected
          setCombinedOffers(offers);
          
          // Extract unique outbound flights from combined offers
          // Group by flight code to deduplicate
          const outboundMap = new Map();
          offers.forEach((offer, idx) => {
            const outbound = offer.OutboundFlight || offer.outboundFlight;
            if (!outbound) {
              return;
            }
            
            const code = outbound.code;
            if (!code) {
              return;
            }
            
            if (!outboundMap.has(code)) {
              outboundMap.set(code, outbound);
            }
          });
          
          const outboundFlights = Array.from(outboundMap.values());
          
          if (outboundFlights.length === 0) {
            setError('No outbound flights found in combined offers. Please try a different route or date.');
            setFlights([]);
            setReturnFlights([]);
            setCombinedOffers([]);
            setSearchTimestamp(null);
            setLoading(false);
            return;
          }

          // Display outbound flights, return flights will be filtered from stored offers when user selects
          setFlights(outboundFlights);
          setReturnFlights([]); // Clear returns, will be populated from stored combinedOffers when outbound selected
          
          setSearchTimestamp(timestamp);
          setHasSearched(true);
          setLoading(false);
        })
        .catch((err) => {
          // Handle different error types with user-friendly messages
          let errorMessage = 'Failed to search round-trip flights';
          
          if (err?.status === 502) {
            errorMessage = 'Flight search system temporarily unavailable. Please try again in a few minutes.';
          } else if (err?.status === 503 || err?.status === 504) {
            errorMessage = 'Our flight search service is temporarily unavailable. Please try again in a few minutes.';
          } else if (!err?.status && (err?.message?.includes('fetch') || err?.message?.includes('Network'))) {
            errorMessage = 'Unable to connect. Please check your internet connection and try again.';
          } else if (err?.message) {
            errorMessage = err.message;
          }
          
          console.error('[ROUND-TRIP SEARCH ERROR]', { error: err, message: errorMessage });
          setError(errorMessage);
          setFlights([]);
          setReturnFlights([]);
          setCombinedOffers([]);
          setMixedAirlineOffersInfo(null);
          setSearchTimestamp(null);
          setLoading(false);
        });
      return;
    }

    // EXISTING ONE-WAY LOGIC (unchanged)
    // Fire the return pre-fetch IN PARALLEL with the outbound search so both
    // Duffel calls run concurrently (each takes ~20-30 s cold).  The backend
    // caches the result for 30 min, so by the time the user picks an outbound
    // flight the return results are already cached and resolve instantly.
    // We suppress errors here — failures are retried when the user actually
    // selects their outbound flight via searchWithAutoRetry.
    if (nextFormData.returnDate) {
      returnPrefetchRef.current = flightsAPI.search(
        nextFormData.to, nextFormData.from, nextFormData.returnDate, nextFormData.class, {},
      ).catch(() => null);
    }

    // Use searchWithAutoRetry so a 503 (backend timeout on slow Duffel routes) silently
    // retries once after 1.5 s instead of showing an error the user has to dismiss.
    searchWithAutoRetry(nextFormData.from, nextFormData.to, nextFormData.date, nextFormData.class, searchFilters)
      .then((results) => {
        const timestamp = Date.now();
        setFlights(results);
        setSearchTimestamp(timestamp);
        if (results.isCached) setStaleCacheNotice(results.cachedAt); else setStaleCacheNotice(null);
        setHasSearched(true);
      })
      .catch((err) => {
        // Handle different error types with user-friendly messages
        let errorMessage = 'Failed to search flights';
        
        if (err?.status === 502) {
          errorMessage = 'Flight search system temporarily unavailable. Please try again in a few minutes.';
        } else if (err?.status === 503 || err?.status === 504) {
          errorMessage = 'Our flight search service is temporarily unavailable. Please try again in a few minutes.';
        } else if (!err?.status && (err?.message?.includes('fetch') || err?.message?.includes('Network'))) {
          errorMessage = 'Unable to connect. Please check your internet connection and try again.';
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        console.error('[SEARCH ERROR]', { error: err, message: errorMessage });
        setError(errorMessage);
        setFlights([]);
        setSearchTimestamp(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Auto-search: fires once on mount when navigated here via "← Back to results" from FlightDetails
  // (using autoSearchParams from sessionStorage/location.state), or after initial mount.
  // Always fetches fresh results.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!autoSearchParams) return;
    // Consume the navigation state so a page refresh doesn't re-trigger the search.
    navigate(location.pathname, { replace: true, state: {} });
    // If forceRefresh is set (from price change modal), bypass cache
    const searchOptions = { directOnly: false, skipHistoryPush: true };
    if (autoSearchParams.forceRefresh) {
      searchOptions.forceRefresh = true;
    }
    runSearchWithFormData(formData, searchOptions);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup sessionStorage restore keys after mount.
  useEffect(() => {
    const canRestore = sessionStorage.getItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY);
    if (canRestore) {
      sessionStorage.removeItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY);
      
      const savedState = savedSearchState;
      // If we have cached flight results, restore instantly without re-fetching
      if (savedState?.flights?.length > 0 && savedState.hasSearched) {
        isRestoringFromCache.current = true;
        
        // Save scroll position to restore after DOM renders
        if (savedState.scrollPosition !== undefined) {
          scrollPositionToRestore.current = savedState.scrollPosition;
        }
        
        // Mark as searched without triggering loading state
        setHasSearched(true);
        
        // Clear restoration flag after a short delay
        setTimeout(() => {
          isRestoringFromCache.current = false;
        }, 500);
      } else if (savedState && savedState.hasSearched && !hasSearched) {
        // No cached results available - re-fetch (will show loading shell)
        if (savedState.scrollPosition !== undefined) {
          scrollPositionToRestore.current = savedState.scrollPosition;
        }
        runSearchWithFormData(savedState.formData, { directOnly: savedState.directOnlySearch, skipHistoryPush: true });
      }
    } else if (!autoSearchParams && !rescheduleContext) {
      // Fresh visit — clear any stale stored results.
      sessionStorage.removeItem(SEARCH_RESULTS_STORAGE_KEY);
    }
    
    // Check if current in-memory results have expired
    if (searchTimestamp && !autoSearchParams) {
      const ageMs = Date.now() - searchTimestamp;
      const ageMinutes = Math.floor(ageMs / 60000);
      if (ageMs > SEARCH_RESULTS_MAX_AGE_MS) {
        // Results expired - clear them
        setFlights([]);
        setReturnFlights([]);
        setSelectedOutboundFlight(null);
        setSelectedReturnFlight(null);
        setSearchTimestamp(null);
        setHasSearched(false);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Restore scroll position after results are rendered
  useEffect(() => {
    if (scrollPositionToRestore.current !== null && !loading && !returnLoading) {
      // Check if we have results to scroll to (either one-way or round-trip)
      const hasResults = flights.length > 0 || returnFlights.length > 0;
      if (hasResults) {
        // For cache restoration, scroll immediately; for fresh search, add delay
        const delay = isRestoringFromCache.current ? 50 : 150;
        
        setTimeout(() => {
          window.scrollTo({
            top: scrollPositionToRestore.current,
            behavior: 'instant'
          });
          
          // Focus on results for better accessibility (skip outline for visual cleanliness)
          if (resultsRef.current) {
            resultsRef.current.setAttribute('tabindex', '-1');
            resultsRef.current.focus({ preventScroll: true });
            resultsRef.current.style.outline = 'none';
          }
          
          scrollPositionToRestore.current = null;
        }, delay);
      }
    }
  }, [flights, returnFlights, loading, returnLoading]);

  const handleDealSelect = (deal) => {
    const nextFormData = {
      ...formData,
      from: deal.from,
      to: deal.to,
      date: addDaysToDateString(todayDate, deal.dayOffset),
      returnDate: '',
      class: 'Economy',
    };

    setTripType('one-way');
    setDirectOnlySearch(false);
    setFormData(nextFormData);
    setFieldErrors({});
    runSearchWithFormData(nextFormData, { directOnly: false });
  };

  const handleOtherSortChange = (nextSortBy) => {
    setOtherSortBy(nextSortBy);
    setQuickSortPreset('other');
    setFilters((prev) => ({
      ...prev,
      sortBy: nextSortBy,
    }));
  };

  const handleDateBrowserSelect = (dateStr) => {
    if (dateStr < todayDate) return;
    const nextFormData = { ...formData, date: dateStr };
    
    // For round trips, always ensure returnDate is set and valid
    if (isRoundTrip) {
      // If no return date, or return date is before new outbound date, adjust it
      if (!formData.returnDate || formData.returnDate <= dateStr) {
        nextFormData.returnDate = addDaysToDateString(dateStr, DEFAULT_ROUND_TRIP_OFFSET_DAYS);
      } else {
        // Preserve existing return date if it's still valid
        nextFormData.returnDate = formData.returnDate;
      }
    }
    
    setFormData(nextFormData);
    runSearchWithFormData(nextFormData);
  };

  const handleDateWindowShift = (delta) => {
    const currentStart = dateWindowStart ?? addDaysToDateString(formData.date, -3);
    setDateWindowStart(addDaysToDateString(currentStart, delta));
  };

  const handleReturnDateBrowserSelect = (dateStr) => {
    if (dateStr < todayDate) return;
    
    // For round-trip mode, re-run the full round-trip search with new return date
    if (isRoundTrip && tripType === 'round-trip') {
      const nextFormData = { 
        ...formData, 
        returnDate: dateStr 
      };
      
      setFormData(nextFormData);
      
      // Clear selections and trigger new round-trip search
      setSelectedOutboundFlight(null);
      setSelectedReturnFlight(null);
      setReturnFlights([]);
      setCombinedOffers([]);
      
      runSearchWithFormData(nextFormData);
      return;
    }
    
    // Legacy one-way fallback (for backward compatibility if trip type changes)
    const nextFormData = { ...formData, returnDate: dateStr };
    setFormData(nextFormData);
    setReturnFlights([]);
    setSelectedReturnFlight(null);
    setReturnError('');
    setReturnLoading(true);
    setReturnDateWindowStart(null);
    const searchFilters = directOnlySearch ? { type: 'Direct' } : {};
    searchWithAutoRetry(formData.to, formData.from, dateStr, formData.class, searchFilters)
      .then((results) => {
        setReturnFlights(results);
        if (!Array.isArray(results) || results.length === 0) {
          setReturnError('We\'re working to bring return flights for this route. Try a different date or check back soon!');
        }
      })
      .catch((err) => {
        // Handle different error types with user-friendly messages
        let errorMessage = 'Failed to fetch return flights. Please try again.';
        
        if (err?.status === 502) {
          errorMessage = 'Flight search system temporarily unavailable. Please try again in a few minutes.';
        } else if (err?.status === 503 || err?.status === 504) {
          errorMessage = 'Flight search service temporarily unavailable. Please try again in a few minutes.';
        } else if (!err?.status && (err?.message?.includes('fetch') || err?.message?.includes('Network'))) {
          errorMessage = 'Connection lost. Please check your internet connection.';
        } else if (err?.message) {
          errorMessage = err.message;
        }
        
        console.error('[RETURN FLIGHT ERROR]', { error: err, message: errorMessage });
        setReturnError(errorMessage);
        setReturnFlights([]);
      })
      .finally(() => setReturnLoading(false));
  };

  const handleReturnDateWindowShift = (delta) => {
    const currentStart = returnDateWindowStart ?? addDaysToDateString(formData.returnDate, -3);
    setReturnDateWindowStart(addDaysToDateString(currentStart, delta));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setError('');
    setSelectedOutboundFlight(null);
    setReturnFlights([]);
    const nextFieldErrors = getDateValidationErrors();
    setFieldErrors(nextFieldErrors);

    // Airport validation
    if (!formData.from.trim() || !formData.to.trim()) {
      setError('Please select both departure and arrival airports.');
      return;
    }

    // Airport code format validation
    const fromValidation = validateAirportCode(formData.from, 'Departure city');
    if (!fromValidation.valid) {
      setError(fromValidation.error);
      return;
    }

    const toValidation = validateAirportCode(formData.to, 'Arrival city');
    if (!toValidation.valid) {
      setError(toValidation.error);
      return;
    }

    // Prevent same airport selection
    const airportValidation = validateDifferentAirports(formData.from, formData.to);
    if (!airportValidation.valid) {
      setError(airportValidation.error);
      return;
    }

    // Passenger mix validation (infant rules, adult supervision)
    const passengerValidation = validatePassengerMix(
      formData.adults,
      formData.children,
      formData.infants
    );
    if (!passengerValidation.valid) {
      setError(passengerValidation.error);
      return;
    }

    // Date range validation (not too far in future)
    const dateRangeValidation = validateSearchDateRange(formData.date, formData.returnDate);
    if (!dateRangeValidation.valid) {
      setError(dateRangeValidation.error);
      return;
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setError('Please correct the highlighted travel dates.');
      return;
    }

    runSearchWithFormData(formData, { directOnly: directOnlySearch });
  };

  // Navigate to flight details — shared by normal flow and reschedule confirm
  const proceedWithSelectedFlight = (flight, extraState = {}) => {
    try {
      // Try storing full results for instant restoration on back button
      sessionStorage.setItem(SEARCH_RESULTS_STORAGE_KEY, JSON.stringify({
        formData,
        hasSearched,
        tripType,
        directOnlySearch,
        selectedCurrency,
        quickSortPreset,
        scrollPosition: window.scrollY || window.pageYOffset,
        searchTimestamp,
        flights, // Store full results for instant back-button UX
        filters,
      }));
      sessionStorage.setItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY, 'true');
    } catch (e) {
      // Quota exceeded - try storing without flight arrays (fallback to re-fetch on restore)
      try {
        sessionStorage.setItem(SEARCH_RESULTS_STORAGE_KEY, JSON.stringify({
          formData,
          hasSearched,
          tripType,
          directOnlySearch,
          selectedCurrency,
          quickSortPreset,
          scrollPosition: window.scrollY || window.pageYOffset,
          searchTimestamp,
        }));
        sessionStorage.setItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY, 'true');
      } catch (fallbackError) {
        // Silently fail if sessionStorage is unavailable
      }
    }
    navigate('/flight-details', {
      state: {
        flight,
        returnFlight: null,
        trip: {
          tripType,
          departureDate: formData.date,
          returnDate: formData.returnDate,
          tickets: totalTravellers,
          passengerType: totalTravellers === 1 && formData.adults === 1 ? 'Adult' : 'Mixed',
          travelClass: formData.class,
          passengers: {
            adults: formData.adults,
            children: formData.children,
            infants: formData.infants,
          },
        },
        selectedCurrency,
        exchangeRates,
        ...extraState,
      },
    });
  };

  // Helper function to filter combined offers and display matching return flights
  const filterAndDisplayReturns = (offers, selectedOutbound) => {
    // CRITICAL: Filter combined offers to ONLY show return flights from offers 
    // that contain the exact selected outbound flight. This ensures pricing consistency.
    const matchingOffers = offers.filter(offer => {
      const outbound = offer.OutboundFlight || offer.outboundFlight;
      if (!outbound) {
        return false;
      }
      
      // STRICT MATCHING: Flight code must match exactly
      // Flight code includes route, time, and fare details
      const codeMatch = outbound.code === selectedOutbound.code;
      
      // BACKUP MATCHING: For offers where code might differ slightly,
      // verify by core flight details (time, airline, route)
      const detailsMatch = 
        outbound.departureTime === selectedOutbound.departureTime &&
        outbound.arrivalTime === selectedOutbound.arrivalTime &&
        outbound.airline === selectedOutbound.airline &&
        outbound.from === selectedOutbound.from &&
        outbound.to === selectedOutbound.to;
      
      // SEGMENT MATCHING: For multi-segment flights, verify all segments match
      let segmentsMatch = false;
      if (outbound.segments?.length > 0 && selectedOutbound.segments?.length > 0) {
        if (outbound.segments.length === selectedOutbound.segments.length) {
          segmentsMatch = outbound.segments.every((seg, idx) => {
            const selectedSeg = selectedOutbound.segments[idx];
            return seg.departureTime === selectedSeg.departureTime &&
                   seg.arrivalTime === selectedSeg.arrivalTime &&
                   seg.carrierCode === selectedSeg.carrierCode &&
                   seg.flightNumber === selectedSeg.flightNumber;
          });
        }
      }
      
      const isMatch = codeMatch || detailsMatch || segmentsMatch;
      
      return isMatch;
    });

    if (matchingOffers.length === 0) {
      
      setReturnError('Unable to find return flights for this outbound option. Please try a different flight or search again.');
      setReturnFlights([]);
      setReturnLoading(false);
      return;
    }

    // Transform combined offers into return flights with pricing data
    // Each combined offer becomes a separate return flight option (different fare classes, airlines, etc.)
    const enrichedReturns = [];
    
    matchingOffers.forEach((offer, idx) => {
      const returnFlight = offer.ReturnFlight || offer.returnFlight;
      const outboundFlight = offer.OutboundFlight || offer.outboundFlight;
      
      if (!returnFlight) {
        return;
      }
      
      const returnCode = returnFlight.code;
      if (!returnCode) {
        return;
      }
      
      const totalPrice = offer.TotalPrice || offer.totalPrice || 0;
      const currency = offer.Currency || offer.currency || 'AUD';
      const combinedOfferId = offer.CombinedOfferId || offer.combinedOfferId;
      
      if (!combinedOfferId) {
        return;
      }
      
      // Use actual prices from Duffel combined offer if available
      const outboundPrice = outboundFlight?.price || selectedOutbound.price;
      const returnPrice = returnFlight.price || (totalPrice - outboundPrice);
      
      // PRICING VERIFICATION: Ensure base prices sum correctly
      const baseSum = outboundPrice + returnPrice;
      const priceDifference = Math.abs(totalPrice - baseSum);
      
      // Add each combined offer as a separate return flight option
      // This allows showing multiple fare classes for the same flight
      enrichedReturns.push({
        ...returnFlight,
        // Use combined offer ID as unique identifier
        code: `${returnFlight.code}-${combinedOfferId.slice(-8)}`, // Make code unique per offer
        originalCode: returnFlight.code, // Keep original for reference
        combinedOfferId: combinedOfferId,
        combinedOfferPrice: totalPrice,
        combinedOfferCurrency: currency,
        outboundPrice: outboundPrice,
        returnOnlyPrice: returnPrice, // Use actual return price from Duffel
        combinedSavings: 0,
      });
    });
    
    if (enrichedReturns.length === 0) {
      setReturnError('No return flights found in combined offers. Please try searching again.');
      setReturnFlights([]);
      setReturnLoading(false);
      return;
    }

    // enrichedReturns now have combinedOfferPrice, returnOnlyPrice, etc.
    setReturnFlights(enrichedReturns);
    setReturnLoading(false);
  };

  const handleSelectFlight = (flight) => {
    // Log search result age for monitoring in development only
    if (process.env.NODE_ENV === 'development' && searchTimestamp) {
      const ageMinutes = Math.floor((Date.now() - searchTimestamp) / 60000);
      if (ageMinutes >= 10) {
        console.warn(`[FLIGHT SELECTION] Search results are ${ageMinutes} min old - offer may be close to expiry!`);
      }
    }
    
    if (isRoundTrip && !selectedOutboundFlight) {
      setSelectedOutboundFlight(flight);
      setSelectedReturnFlight(null);
      setReturnError('');
      setError('');

      // CRITICAL: Clear airline filter when selecting outbound so return flights aren't filtered
      // Return flights can be from different airlines (interline itineraries)
      setFilters((prev) => ({
        ...prev,
        airlines: [], // Clear airline filter to show all return options
      }));

      // NEW HYBRID APPROACH: Use stored combined offers (already fetched during initial search)
      // Filter to match selected outbound and extract return flights with combined pricing
      // This is INSTANT - no API call needed!
      setReturnLoading(true);
      setReturnFlights([]); // Clear previous return flights
      
      // Use stored combined offers from initial search
      if (!combinedOffers || combinedOffers.length === 0) {
        // No combined offers available - this shouldn't happen if initial search succeeded
        setReturnError('Combined offers not loaded. Please search again.');
        setReturnFlights([]);
        setReturnLoading(false);
        return;
      }

      // Process stored combined offers (instant!)
      filterAndDisplayReturns(combinedOffers, flight);
      
      return;
    }

    if (isRoundTrip && selectedOutboundFlight) {
      // This is the return flight selection - same as isSelectingReturn logic
      // Stage the return flight in the sticky bar; user confirms via "View Fares"
      setSelectedReturnFlight(flight);
      return;
    }

    if (isSelectingReturn) {
      // Stage the return flight in the sticky bar; user confirms via "View Fares"
      setSelectedReturnFlight(flight);
      return;
    }

    // Reschedule mode: skip estimate modal, go straight to passenger details
    // Full pricing (new flight total) is shown at BookingPreview before payment
    if (rescheduleContext) {
      const rescheduleInfo = {
        bookingId: rescheduleContext.bookingId,
        bookingRef: rescheduleContext.bookingRef || '',
        originalFrom: rescheduleContext.from,
        originalTo: rescheduleContext.to,
      };
      proceedWithSelectedFlight(flight, { rescheduleInfo });
      return;
    }

    // Auth gate moved to BookingPreview — guests can now fill passenger details before sign-in
    proceedWithSelectedFlight(flight);
  };

  const handleRetryReturnSearch = () => {
    if (!selectedOutboundFlight) {
      setReturnError('Please select an outbound flight first.');
      return;
    }

    setReturnError('');
    setReturnLoading(true);

    // Re-search return flights (sequential search)
    
    const searchFilters = directOnlySearch ? { type: 'Direct' } : {};
    
    flightsAPI.search(
      formData.to,         // Return: from destination back to origin
      formData.from,
      formData.returnDate,
      formData.class,
      searchFilters
    )
      .then((returnFlights) => {
        if (returnFlights.length === 0) {
          setReturnError('We\'re working to bring return flights for this route. Try a different date or check back soon!');
          setReturnFlights([]);
          setReturnLoading(false);
          return;
        }

        setReturnFlights(returnFlights);
        setReturnLoading(false);
      })
      .catch((err) => {
        console.error('[RETRY RETURN] Return flight search failed:', err);
        setReturnError(err?.message || 'Failed to load return flights. Please try again.');
        setReturnFlights([]);
        setReturnLoading(false);
      });
  };

  const buildTrip = () => ({
    tripType,
    departureDate: formData.date,
    returnDate: formData.returnDate,
    tickets: totalTravellers,
    passengerType: totalTravellers === 1 && formData.adults === 1 ? 'Adult' : 'Mixed',
    travelClass: formData.class,
    passengers: {
      adults: formData.adults,
      children: formData.children,
      infants: formData.infants,
    },
  });

  const handleViewFares = () => {
    if (!selectedOutboundFlight || !selectedReturnFlight) return;

    // Save round-trip results for instant back-button restoration
    try {
      sessionStorage.setItem(SEARCH_RESULTS_STORAGE_KEY, JSON.stringify({
        formData,
        hasSearched,
        tripType,
        directOnlySearch,
        selectedCurrency,
        quickSortPreset,
        scrollPosition: window.scrollY || window.pageYOffset,
        searchTimestamp,
        flights, // Outbound flights
        returnFlights, // Return flights
        selectedOutboundFlight,
        selectedReturnFlight,
        filters,
      }));
      sessionStorage.setItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY, 'true');
    } catch (e) {
      // Quota exceeded - try minimal fallback
      try {
        sessionStorage.setItem(SEARCH_RESULTS_STORAGE_KEY, JSON.stringify({
          formData,
          hasSearched,
          tripType,
          directOnlySearch,
          selectedCurrency,
          quickSortPreset,
          scrollPosition: window.scrollY || window.pageYOffset,
          searchTimestamp,
        }));
        sessionStorage.setItem(SEARCH_RESULTS_RESTORE_ALLOWED_KEY, 'true');
      } catch (fallbackError) {
        // Silently fail if sessionStorage is unavailable
      }
    }

    // Auth gate moved to BookingPreview — guests proceed to FlightDetails before sign-in
    navigate('/flight-details', {
      state: {
        flight: selectedOutboundFlight,
        returnFlight: selectedReturnFlight,
        combinedOfferId: selectedReturnFlight?.combinedOfferData?.combinedOfferId || null,
        combinedOfferData: selectedReturnFlight?.combinedOfferData || null,
        combinedOffers: [], // Not needed: backend will match at payment time
        trip: buildTrip(),
        selectedCurrency,
        exchangeRates,
      },
    });
  };

  // Apply client-side filters
  const filteredFlights = useMemo(() => {
    let result = [...visibleActiveFlights];

    if (filters.airlines.length > 0) {
      result = result.filter((flight) => filters.airlines.includes(getFlightAirlineName(flight)));
    }

    if (directOnlySearch) {
      result = result.filter((f) => {
        const typeText = String(f.type || '').toLowerCase();
        return typeText === 'direct' || typeText === 'non-stop' || typeText === 'nonstop' || typeText === '0-stop' || typeText === '0-stops';
      });
    }

    // Apply max price slider filter
    const maxDisplayPrice = convertAmount(filters.maxPrice, 'AUD', selectedCurrency, exchangeRates) * (1 + surchargeRate);
    result = result.filter((flight) => (
      convertAmount(
        flight.price,
        flight.priceBreakdown?.currency || 'AUD',
        selectedCurrency,
        exchangeRates,
      ) * (1 + surchargeRate) <= maxDisplayPrice
    ));

    if (filters.stops.length > 0) {
      const allowedStopCounts = new Set(
        filters.stops
          .map((key) => STOP_FILTER_OPTIONS.find((option) => option.key === key)?.value)
          .filter((value) => Number.isInteger(value)),
      );

      result = result.filter((flight) => allowedStopCounts.has(getStopCountFromFlight(flight)));
    }

    if (filters.departureWindow !== 'any') {
      result = result.filter((flight) => matchesTimeFilter(flight.departureTime, filters.departureWindow));
    }

    if (filters.arrivalWindow !== 'any') {
      result = result.filter((flight) => matchesTimeFilter(flight.arrivalTime, filters.arrivalWindow));
    }

    // Apply sorting
    switch (filters.sortBy) {
      case 'price':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'time':
        result.sort((a, b) => parseDurationToMinutes(a.duration) - parseDurationToMinutes(b.duration));
        break;
      case 'departure':
      default:
        result.sort((a, b) => a.departureTime.localeCompare(b.departureTime));
    }

    return result;
  }, [visibleActiveFlights, filters, directOnlySearch, selectedCurrency, exchangeRates, surchargeRate]);

  /**
   * Groups filteredFlights by physical flight (carrier + flight number + date + departure).
   * Each entry is an array sorted cheapest-first; entries with only 1 offer behave normally.
   */
  const groupedFlights = useMemo(() => {
    const groups = new Map();
    for (const flight of filteredFlights) {
      const key = getFlightGroupKey(flight);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(flight);
    }
    // Sort each group cheapest first so the representative card shows the lowest price
    for (const group of groups.values()) {
      group.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
    }
    return Array.from(groups.values());
  }, [filteredFlights]);

  const shouldAnimateResults = hasSearched && !isLoadingResults && filteredFlights.length > 0;

  const dateWindowChips = useMemo(() => {
    if (!hasSearched) return [];
    const windowStart = dateWindowStart ?? addDaysToDateString(formData.date, -3);
    return Array.from({ length: 7 }, (_, i) => addDaysToDateString(windowStart, i));
  }, [hasSearched, dateWindowStart, formData.date]);

  const returnDateWindowChips = useMemo(() => {
    if (!isSelectingReturn || !formData.returnDate) return [];
    const windowStart = returnDateWindowStart ?? addDaysToDateString(formData.returnDate, -3);
    return Array.from({ length: 7 }, (_, i) => addDaysToDateString(windowStart, i));
  }, [isSelectingReturn, returnDateWindowStart, formData.returnDate]);

  const loadingNarrative = getLoadingNarrative({
    isReturn: returnLoading,
    origin: returnLoading ? formData.to : formData.from,
    destination: returnLoading ? formData.from : formData.to,
    outboundDate: formData.date,
    returnDate: formData.returnDate,
    travelClass: formData.class,
    directOnly: directOnlySearch,
  });

  return (
    <div className={`search-flights-container${isSelectingReturn && showRoundTripBar ? ' rt-bar-active' : ''}${rescheduleContext ? ' reschedule-mode' : ''}${!hasSearched ? ' search-flights-container--hero' : ''}`} style={{ overflowX: 'hidden' }}>
      {rescheduleContext && (
        <div className="reschedule-mode-banner" role="status">
          <span className="reschedule-mode-banner__icon" aria-hidden="true">🔄</span>
          <span>
            <strong>Rescheduling</strong>
            {rescheduleContext.from && rescheduleContext.to
              ? ` — ${rescheduleContext.from} → ${rescheduleContext.to}`
              : ''}
            {' '}— Route is locked. Change the date and select a new flight.
            {rescheduleContext.airline
              ? ` Showing ${rescheduleContext.airline} flights only.`
              : rescheduleContext.carrierCode
                ? ` Showing ${rescheduleContext.carrierCode} flights only.`
                : ''}
          </span>
        </div>
      )}
      
      {showDomesticNotice && (
        <div className="air-service-notice" role="alert" style={{
          backgroundColor: '#e3f2fd',
          border: '1px solid #2196f3',
          borderRadius: '8px',
          padding: '16px 20px',
          margin: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
        }}>
          <span style={{ fontSize: '24px', flexShrink: 0, marginTop: '2px' }} aria-hidden="true">✈️</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1565c0', marginBottom: '4px' }}>
              Coming Soon: Australian Domestic Flights
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.5', color: '#01579b' }}>
              We're working to bring you Australian domestic flight options. Meanwhile, explore our wide range of international flights!
            </div>
          </div>
          <button
            onClick={dismissDomesticNotice}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px 8px',
              lineHeight: 1,
              flexShrink: 0,
              marginTop: '-4px',
            }}
            aria-label="Dismiss notice"
          >
            ×
          </button>
        </div>
      )}

      {/* Full-Screen Background with Form Overlay - Only show when not searched */}
      {!hasSearched && (
        <div 
          className="relative w-full"
          style={{ 
            minHeight: '80vh',
            borderRadius: 'clamp(16px, 4vw, 60px)',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}
        >
          {/* Background Image - Full Width */}
          <div className="absolute inset-0" style={{ background: '#1c2d3e' }}>
            <img src={backgroundImage} className="absolute inset-0 w-full h-full object-cover" alt="Travel background" fetchpriority="high" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45))' }}></div>
          </div>

          {/* Content Container */}
          <div className="relative z-10 min-h-full flex flex-col lg:flex-row items-start justify-center p-4 sm:p-6 lg:p-8 gap-0">
            {/* Left: Hero Text (Hidden on mobile, shown on lg+) */}
            <div className="hidden lg:flex lg:w-1/2 lg:pr-8 flex-col justify-start max-w-2xl pt-[60px]">
              <div className="text-white">
                <h1 className="text-5xl xl:text-6xl font-bold mb-8 leading-tight drop-shadow-lg">
                  Planning to fly?<br /><span style={{ color: '#0066ff' }}>AirFlyNow</span>
                </h1>
                <div className="flex gap-4 flex-wrap">
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl px-6 py-3 shadow-lg">
                    <div className="text-sm opacity-80 font-medium">Book Flights</div>
                    <div className="text-2xl font-bold">Faster</div>
                  </div>
                  <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl px-6 py-3 shadow-lg">
                    <div className="text-sm opacity-80 font-medium">24/7 Support</div>
                    <div className="text-2xl font-bold">Available</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile / Tablet: compact hero above form */}
            <div className="lg:hidden w-full text-center pt-10 pb-4 px-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight drop-shadow-lg">
                Planning to fly?<br /><span style={{ color: '#0066ff' }}>AirFlyNow</span>
              </h1>
            </div>

            {/* Right: Form Card */}
            <div className="w-full lg:w-[35%] lg:pl-6 max-w-md mx-auto lg:mx-0 pt-0 lg:pt-8">

              {/* Trip Tabs + Currency — OUTSIDE the card, Webjet style */}
              <div className="flex items-center justify-between mb-2 px-1">
                {rescheduleContext?.lockRoute ? (
                  <span className="text-sm text-white font-semibold bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2">One Way — Route locked</span>
                ) : (
                  <div className="flex gap-0 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-1">
                    <button
                      type="button"
                      onClick={() => handleTripTypeChange('one-way')}
                      className={`px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all ${tripType === 'one-way' ? 'bg-white text-gray-900 shadow-sm' : 'text-white hover:bg-white hover:bg-opacity-20'}`}
                    >
                      One Way
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTripTypeChange('round-trip')}
                      className={`px-4 sm:px-5 py-2 rounded-lg text-sm font-semibold transition-all ${tripType === 'round-trip' ? 'bg-white text-gray-900 shadow-sm' : 'text-white hover:bg-white hover:bg-opacity-20'}`}
                    >
                      Return
                    </button>
                  </div>
                )}
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="px-3 py-2 text-sm font-semibold bg-white bg-opacity-20 backdrop-blur-sm text-white border border-white border-opacity-30 rounded-xl focus:outline-none transition-all cursor-pointer"
                >
                  <option value="AUD" className="text-gray-900 bg-white">AUD</option>
                  <option value="USD" className="text-gray-900 bg-white">USD</option>
                  <option value="INR" className="text-gray-900 bg-white">INR</option>
                </select>
              </div>

              {/* Form Card */}
              <div 
                className="bg-white rounded-2xl shadow-2xl p-4 border border-gray-100" 
                style={{ 
                  backdropFilter: 'blur(10px)', 
                  WebkitBackdropFilter: 'blur(10px)',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
                }}
              >
                <SearchFormTailwind
                  tripType={tripType}
                  onTripTypeChange={handleTripTypeChange}
                  formData={formData}
                  onInputChange={handleInputChange}
                  onSwap={handleSwap}
                  directOnlySearch={directOnlySearch}
                  onDirectOnlyChange={setDirectOnlySearch}
                  onSubmit={handleSearch}
                  loading={loading}
                  todayDate={todayDate}
                  minimumReturnDate={minimumReturnDate}
                  fieldErrors={fieldErrors}
                  travellerSummary={travellerSummary}
                  travellerPanelOpen={travellerPanelOpen}
                  setTravellerPanelOpen={setTravellerPanelOpen}
                  travellerPanelRef={travellerPanelRef}
                  handlePassengerCountChange={handlePassengerCountChange}
                  handleClassChange={handleClassChange}
                  classOptions={classOptions}
                  rescheduleContext={rescheduleContext}
                  returnDatePickerRef={returnDatePickerRef}
                  setFormData={setFormData}
                  selectedCurrency={selectedCurrency}
                  setSelectedCurrency={setSelectedCurrency}
                />
                
                {/* Error message inside form card */}
                {error && (
                  <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <span className="text-red-600 text-lg flex-shrink-0">⚠️</span>
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                )}
              </div>

              {/* Bottom padding so form doesn't touch bottom on mobile */}
              <div className="h-6 lg:hidden" />
            </div>
          </div>
        </div>
      )}

      {/* Trust & Support Bar */}
      {!hasSearched && (() => {
        try {
          return (
            <div className="trust-bar">
              <div className="trust-bar-grid">
                <div className="trust-bar-item">
                  <div className="trust-bar-item__icon">💬</div>
                  <div className="trust-bar-item__title">24/7 Support</div>
                  <div className="trust-bar-item__description">{'< 60 min response or check our FAQ page'}</div>
                </div>
                <div className="trust-bar-item">
                  <div className="trust-bar-item__icon">🔒</div>
                  <div className="trust-bar-item__title">Secure Payment</div>
                  <div className="trust-bar-item__description">Bank-level encryption</div>
                </div>
                <div className="trust-bar-item">
                  <div className="trust-bar-item__icon">🛡️</div>
                  <div className="trust-bar-item__title">Fraud Prevention</div>
                  <div className="trust-bar-item__description">Advanced security checks</div>
                </div>
                <div className="trust-bar-item">
                  <div className="trust-bar-item__icon">🌐</div>
                  <div className="trust-bar-item__title">Multi-Currency Pricing</div>
                  <div className="trust-bar-item__description">View prices in AUD, USD, or INR</div>
                </div>
              </div>
            </div>
          );
        } catch (error) {
          console.error('Trust bar render error:', error);
          return null;
        }
      })()}

      {/* Loyalty Points Preview */}
      {!hasSearched && (() => {
        try {
          return (
            <div className="loyalty-preview">
              <div className="loyalty-preview__blob-top" />
              <div className="loyalty-preview__blob-bottom" />
              <div className="loyalty-preview-grid">
                <div className="loyalty-preview__content">
                  <div className="loyalty-preview__badge">
                    ✨ LOYALTY REWARDS
                  </div>
                  <div className="loyalty-preview__title">
                    <span className="loyalty-preview__title-highlight">Earn Rewards</span><br />on Every Booking
                  </div>
                  <div className="loyalty-preview__description">
                    Get rewarded for your travels. Start earning today!
                  </div>
                  {!isAuthenticated && (
                    <button
                      onClick={() => openLoginModal({ returnTo: '/search-flights' })}
                      className="loyalty-preview__join-btn"
                    >
                      Join Free →
                    </button>
                  )}
                </div>
                <div className="loyalty-preview__cards">
                  <div className="loyalty-preview-card">
                    <div className="loyalty-preview-card__title">
                      💰 Earn Cashback
                    </div>
                    <div className="loyalty-preview-card__description">
                      $2000 flight = <span className="loyalty-preview-card__highlight">2000 points</span>
                    </div>
                  </div>
                  <div className="loyalty-preview-card">
                    <div className="loyalty-preview-card__title">
                      ✨ Redeem at Checkout
                    </div>
                    <div className="loyalty-preview-card__description">
                      Use your points as cash when you book your next flight
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        } catch (error) {
          console.error('Loyalty preview render error:', error);
          return null;
        }
      })()}

      {/* Referral Program CTA */}
      {!hasSearched && (() => {
        try {
          return (
            <div style={{
              background: 'white',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              padding: '32px 24px',
              margin: '32px auto',
              maxWidth: '800px',
              textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎁</div>
              <div style={{ 
                color: '#1e293b', 
                fontSize: '24px', 
                fontWeight: '800', 
                marginBottom: '12px' 
              }}>
                Refer a Friend, Both Get $10
              </div>
              <div style={{ 
                color: '#64748b', 
                fontSize: '15px', 
                marginBottom: '24px', 
                lineHeight: '1.6',
                maxWidth: '600px',
                margin: '0 auto 24px'
              }}>
                Share AirFlyNow with friends and you'll both receive $10 credit when they make their first booking
              </div>
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/loyalty')}
                  style={{
                    background: '#0066ff',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 102, 255, 0.3)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 102, 255, 0.3)';
                  }}
                >
                  Get Your Referral Link →
                </button>
              ) : (
                <button
                  onClick={() => openLoginModal({ returnTo: '/search-flights' })}
                  style={{
                    background: '#0066ff',
                    color: 'white',
                    padding: '12px 32px',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0, 102, 255, 0.3)',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 102, 255, 0.3)';
                  }}
                >
                  Sign Up to Start Referring →
                </button>
              )}
            </div>
          );
        } catch (error) {
          console.error('Referral CTA render error:', error);
          return null;
        }
      })()}

      {hasSearched && (
        <div className="results-container" ref={resultsRef}>
          {/* Currency Selector for Results View - Hidden on Mobile */}
          <div className="absolute top-4 right-4 z-20 hidden md:block">
            <CurrencySelector 
              value={selectedCurrency} 
              onChange={setSelectedCurrency}
              disabled={hasSearched && flights.length > 0}
            />
          </div>

          {!isLoadingResults && (
            <button
              type="button"
              className="mobile-filters-btn"
              aria-expanded={filterDrawerOpen}
              onClick={() => setFilterDrawerOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="11" y1="18" x2="13" y2="18" />
              </svg>
              Filters
            </button>
          )}
          {filterDrawerOpen && (
            <div className="mobile-filters-backdrop" onClick={() => setFilterDrawerOpen(false)} aria-hidden="true" />
          )}
          {!isLoadingResults && <div className={`filters-sidebar${filterDrawerOpen ? ' filters-sidebar--open' : ''}`}>
            <div className="filters-sidebar__header">
              <h3>Filters</h3>
              <button
                type="button"
                className="filters-sidebar__close"
                aria-label="Close filters"
                onClick={() => setFilterDrawerOpen(false)}
              >
                ×
              </button>
            </div>
            
            <div className={`filter-section filter-section--stops${collapsedFilters.stops ? ' is-collapsed' : ''}`}>
              <button
                type="button"
                className="filter-section__toggle"
                aria-expanded={!collapsedFilters.stops}
                onClick={() => handleFilterSectionToggle('stops')}
              >
                <span className="filter-section__toggle-label">Stops From {formData.from}</span>
                <span className={`filter-section__toggle-icon${collapsedFilters.stops ? ' is-collapsed' : ''}`}>▾</span>
              </button>
              <div className="filter-section__content stops-filter-list">
                {STOP_FILTER_OPTIONS.map((stopOption) => {
                  const stopStats = stopFilterStats[stopOption.key];
                  const selected = filters.stops.includes(stopOption.key);
                  const minPrice = stopStats?.minPrice;

                  return (
                    <label key={stopOption.key} className="stops-filter-option">
                      <div className="stops-filter-option__left">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => handleStopFilterToggle(stopOption.key)}
                        />
                        <span>{stopOption.label} ({stopStats?.count || 0})</span>
                      </div>
                      <span className="stops-filter-option__price">
                        {minPrice != null ? formatDisplayPrice(minPrice) : '-'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className={`filter-section${collapsedFilters.price ? ' is-collapsed' : ''}`}>
              <button
                type="button"
                className="filter-section__toggle"
                aria-expanded={!collapsedFilters.price}
                onClick={() => handleFilterSectionToggle('price')}
              >
                <span className="filter-section__toggle-label">Max Price ({formatDisplayPrice(filters.maxPrice)})</span>
                <span className={`filter-section__toggle-icon${collapsedFilters.price ? ' is-collapsed' : ''}`}>▾</span>
              </button>
              <div className="filter-section__content slider-wrap">
                <input
                  type="range"
                  name="maxPrice"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  min="400"
                  max={priceSliderMax}
                  step="50"
                />
                <div className="slider-scale">
                  <span>{formatDisplayPrice(400)}</span>
                  <span>{formatDisplayPrice(priceSliderMax)}</span>
                </div>
              </div>
            </div>

            <div className={`filter-section${collapsedFilters.time ? ' is-collapsed' : ''}`}>
              <button
                type="button"
                className="filter-section__toggle"
                aria-expanded={!collapsedFilters.time}
                onClick={() => handleFilterSectionToggle('time')}
              >
                <span className="filter-section__toggle-label">Departure From {formData.from}</span>
                <span className={`filter-section__toggle-icon${collapsedFilters.time ? ' is-collapsed' : ''}`}>▾</span>
              </button>
              <div className="filter-section__content time-chip-grid" role="group" aria-label="Departure time filter">
                {TIME_FILTER_OPTIONS.map((timeOption) => {
                  const selected = filters.departureWindow === timeOption.key;
                  return (
                    <button
                      key={timeOption.key}
                      type="button"
                      className={`time-chip${selected ? ' is-selected' : ''}`}
                      onClick={() => handleTimeWindowSelect('departure', timeOption.key)}
                    >
                      <span className="time-chip__icon">{timeOption.icon}</span>
                      <span className="time-chip__label">{timeOption.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`filter-section${collapsedFilters.arrival ? ' is-collapsed' : ''}`}>
              <button
                type="button"
                className="filter-section__toggle"
                aria-expanded={!collapsedFilters.arrival}
                onClick={() => handleFilterSectionToggle('arrival')}
              >
                <span className="filter-section__toggle-label">Arrival at {formData.to}</span>
                <span className={`filter-section__toggle-icon${collapsedFilters.arrival ? ' is-collapsed' : ''}`}>▾</span>
              </button>
              <div className="filter-section__content time-chip-grid" role="group" aria-label="Arrival time filter">
                {TIME_FILTER_OPTIONS.map((timeOption) => {
                  const selected = filters.arrivalWindow === timeOption.key;
                  return (
                    <button
                      key={timeOption.key}
                      type="button"
                      className={`time-chip${selected ? ' is-selected' : ''}`}
                      onClick={() => handleTimeWindowSelect('arrival', timeOption.key)}
                    >
                      <span className="time-chip__icon">{timeOption.icon}</span>
                      <span className="time-chip__label">{timeOption.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`filter-section${collapsedFilters.airlines ? ' is-collapsed' : ''}`}>
              <div className="filter-section__title-row">
                <button
                  type="button"
                  className="filter-section__toggle"
                  aria-expanded={!collapsedFilters.airlines}
                  onClick={() => handleFilterSectionToggle('airlines')}
                >
                  <span className="filter-section__toggle-label">Airlines</span>
                  <span className={`filter-section__toggle-icon${collapsedFilters.airlines ? ' is-collapsed' : ''}`}>▾</span>
                </button>
                {filters.airlines.length > 0 && (
                  <button
                    type="button"
                    className="filter-clear-btn"
                    onClick={() => setFilters((prev) => ({ ...prev, airlines: [] }))}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="filter-section__content airline-filter-list" role="group" aria-label="Filter by airline">
                {airlineFilterOptions.length > 0 ? (
                  airlineFilterOptions.map((airlineOption) => {
                    const airlineName = airlineOption.name;
                    const checked = filters.airlines.includes(airlineName);
                    
                    // Use Duffel logo URL from API (SVG format for best quality)
                    const logoUrl = airlineOption.logoUrl || null;
                    
                    // FALLBACK: Kiwi.com CDN (commented out - only for testing until Duffel logos verified)
                    // const logoUrl = airlineOption.logoCode ? `https://images.kiwi.com/airlines/64/${airlineOption.logoCode}.png` : '';
                    
                    return (
                      <label key={airlineName} className={`airline-filter-option${checked ? ' is-selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleAirlineFilterToggle(airlineName)}
                        />
                        <span className="airline-filter-option__brand">
                          {logoUrl ? (
                            <img
                              className="airline-filter-option__logo"
                              src={logoUrl}
                              alt=""
                              aria-hidden="true"
                              onError={(event) => {
                                event.currentTarget.style.display = 'none';
                                const fallback = event.currentTarget.nextElementSibling;
                                if (fallback) {
                                  fallback.style.display = 'inline-flex';
                                }
                              }}
                            />
                          ) : null}
                          <span className="airline-filter-option__logo-fallback" style={logoUrl ? { display: 'none' } : undefined} aria-hidden="true">
                            {getAirlineInitials(airlineName)}
                          </span>
                          <span className="airline-filter-option__name">{airlineName}</span>
                        </span>
                        <span className="airline-filter-option__count">({airlineOption.count})</span>
                      </label>
                    );
                  })
                ) : (
                  <span className="airline-filter-empty">
                    {shouldShowLoadingShell ? 'Airlines will appear once results finish loading.' : 'No airlines available for this search.'}
                  </span>
                )}
              </div>
            </div>


          </div>}

          <div className={`results-section${isLoadingResults ? ' results-section--fullwidth' : ''}`}>
            <div className="results-section__header">
              <h2
                aria-live="polite"
                aria-atomic="true"
              >
                {isLoadingResults
                  ? `Searching ${loadingNarrative.metaRoute}`
                  : isSelectingReturn
                  ? `Select your return flight — ${selectedOutboundFlight.to} to ${selectedOutboundFlight.from}`
                  : error
                  ? `${formData.from} → ${formData.to}`
                  : `${groupedFlights.length} flight${groupedFlights.length !== 1 ? 's' : ''} for ${totalTravellers} traveller${totalTravellers > 1 ? 's' : ''}`}
              </h2>
              <span className="results-section__currency-note">Prices shown in {selectedCurrency}</span>
            </div>

            {!isLoadingResults && !isSelectingReturn && (
              <div className="date-browser" role="group" aria-label="Browse departure dates">
                <button
                  type="button"
                  className="date-browser__nav"
                  aria-label="Go back 7 days"
                  onClick={() => handleDateWindowShift(-7)}
                >
                  ‹
                </button>
                <div className="date-browser__chips">
                  {dateWindowChips.map((dateStr) => {
                    const isActive = dateStr === formData.date;
                    const isPast = dateStr < todayDate;
                    return (
                      <button
                        key={dateStr}
                        type="button"
                        className={`date-browser__chip${isActive ? ' is-active' : ''}${isPast ? ' is-past' : ''}`}
                        onClick={() => handleDateBrowserSelect(dateStr)}
                        disabled={isPast}
                        aria-pressed={isActive}
                      >
                        <span className="date-browser__chip-day">{getDayAbbrev(dateStr)}</span>
                        <span className="date-browser__chip-date">{formatChipDate(dateStr)}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="date-browser__nav"
                  aria-label="Go forward 7 days"
                  onClick={() => handleDateWindowShift(7)}
                >
                  ›
                </button>
              </div>
            )}

            {!isLoadingResults && visibleActiveFlights.length > 0 && (
              <div className="results-quick-options" role="group" aria-label="Quick result options">
                <button
                  type="button"
                  className={`quick-option-card${quickSortPreset === 'cheapest' ? ' is-active' : ''}`}
                  onClick={() => applyQuickPreset('cheapest')}
                >
                  <span className="quick-option-card__label">
                    <span className="quick-option-card__icon" aria-hidden="true">★</span>
                    Cheapest
                  </span>
                  <span className="quick-option-card__meta">
                    {quickOptionInsights.cheapest
                      ? `${formatDisplayPrice(quickOptionInsights.cheapest.price, quickOptionInsights.cheapest.priceBreakdown?.currency || 'AUD')} · ${quickOptionInsights.cheapest.duration || '~'}`
                      : 'No option'}
                  </span>
                </button>

                <button
                  type="button"
                  className={`quick-option-card${quickSortPreset === 'nonstop' ? ' is-active' : ''}`}
                  onClick={() => applyQuickPreset('nonstop')}
                >
                  <span className="quick-option-card__label">
                    <span className="quick-option-card__icon" aria-hidden="true">✈</span>
                    Non Stop First
                  </span>
                  <span className="quick-option-card__meta">
                    {quickOptionInsights.nonstop
                      ? `${formatDisplayPrice(quickOptionInsights.nonstop.price, quickOptionInsights.nonstop.priceBreakdown?.currency || 'AUD')} · ${quickOptionInsights.nonstop.duration || '~'}`
                      : 'No non-stop found'}
                  </span>
                </button>

                <button
                  type="button"
                  className={`quick-option-card${quickSortPreset === 'preferred' ? ' is-active' : ''}`}
                  onClick={() => applyQuickPreset('preferred')}
                >
                  <span className="quick-option-card__label">
                    <span className="quick-option-card__icon" aria-hidden="true">⬢</span>
                    You May Prefer
                  </span>
                  <span className="quick-option-card__meta">
                    {quickOptionInsights.preferred
                      ? `${formatDisplayPrice(quickOptionInsights.preferred.price, quickOptionInsights.preferred.priceBreakdown?.currency || 'AUD')} · ${quickOptionInsights.preferred.duration || '~'}`
                      : 'No option'}
                  </span>
                </button>

                <label className={`quick-option-card quick-option-card--select${quickSortPreset === 'other' ? ' is-active' : ''}`}>
                  <span className="quick-option-card__label">
                    <span className="quick-option-card__icon" aria-hidden="true">☰</span>
                    Other Sort
                  </span>
                  <select
                    className="quick-option-card__select"
                    value={otherSortBy}
                    onChange={(event) => handleOtherSortChange(event.target.value)}
                  >
                    <option value="departure">Departure Time</option>
                    <option value="price">Price</option>
                    <option value="time">Duration</option>
                  </select>
                </label>
              </div>
            )}

            {shouldShowLoadingShell && (
              <SearchLoadingShell
                badge={loadingNarrative.badge}
                title={loadingNarrative.title}
                message={loadingNarrative.message}
                status={loadingNarrative.status}
                metaItems={[
                  `${totalTravellers} traveller${totalTravellers > 1 ? 's' : ''}`,
                  loadingNarrative.metaRoute,
                  loadingNarrative.metaDate,
                  loadingNarrative.metaMode,
                ]}
                isExiting={loadingShellExiting}
              />
            )}

            <div className={`flights-list${shouldAnimateResults ? ' flights-list--ready' : ''}`}>
              {isLoadingResults ? (
                Array.from({ length: LOADING_SKELETON_COUNT }, (_, index) => (
                  <div className="flight-result-skeleton" key={`flight-skeleton-${index}`} style={{ '--skeleton-index': index }}>
                    <div className="flight-result-skeleton__airline">
                      <span className="flight-result-skeleton__logo shimmer-block"></span>
                      <div className="flight-result-skeleton__stack">
                        <span className="shimmer-block shimmer-block--title"></span>
                        <span className="shimmer-block shimmer-block--code"></span>
                      </div>
                    </div>
                    <div className="flight-result-skeleton__timeline">
                      <span className="shimmer-block shimmer-block--time"></span>
                      <span className="shimmer-block shimmer-block--duration"></span>
                      <span className="shimmer-block shimmer-block--time"></span>
                    </div>
                    <div className="flight-result-skeleton__chips">
                      <span className="shimmer-block shimmer-block--chip"></span>
                      <span className="shimmer-block shimmer-block--chip"></span>
                    </div>
                    <div className="flight-result-skeleton__price">
                      <span className="shimmer-block shimmer-block--price"></span>
                      <span className="shimmer-block shimmer-block--button"></span>
                    </div>
                  </div>
                ))
              ) : filteredFlights.length > 0 ? (() => {
                  // Use smart tagging to assign badges
                  const representativeFlights = groupedFlights.map((grp) => grp[0]);
                  const flightTags = assignFlightTags(
                    representativeFlights,
                    (flight) => getDisplayPriceAmount(flight.price, flight.priceBreakdown?.currency || 'AUD')
                  );

                  // Split into suggested (flights with badges) and rest
                  const suggestedGroups = [];
                  const restGroups = [];

                  for (const grp of groupedFlights) {
                    const badge = flightTags.get(grp[0].code);
                    if (badge) {
                      suggestedGroups.push({ grp, badge });
                    } else {
                      restGroups.push(grp);
                    }
                  }

                  const renderFlightCard = (group, index, badge) => {
                    const flight = group[0];
                    const fareOptionCount = group.length;
                    const departureDateLabel = formatShortDate(flight.date);
                    const arrivalDateLabel = flight.arrivalDate
                      ? formatShortDate(flight.arrivalDate)
                      : getArrivalDateLabel(flight.date, flight.departureTime, flight.arrivalTime);
                    
                    // NEW: Check if this is a return flight with combined offer pricing
                    const isReturnWithCombined = isSelectingReturn && flight.combinedOfferPrice != null;
                    
                    // Calculate display price based on context
                    let displayPrice, displayPriceAmount, priceLabel, showPlusSign;
                    
                    if (isReturnWithCombined) {
                      // For return flights with combined offer, show the additional cost with "+" prefix
                      // Use actual return price from the combined offer
                      const returnOnlyPrice = flight.returnOnlyPrice || flight.price;
                      // IMPORTANT: Use selectedOutboundFlight price (what user actually selected), not bundled outbound
                      const actualOutboundPrice = selectedOutboundFlight?.price || flight.outboundPrice;
                      
                      // Simple pricing: Apply 1.05 to each individually
                      // Math: (outbound × 1.05) + (return × 1.05) = (outbound + return) × 1.05
                      const outboundWithSurcharge = getCombinedOfferPriceAmount(actualOutboundPrice, flight.combinedOfferCurrency || 'AUD');
                      const returnWithSurcharge = getCombinedOfferPriceAmount(returnOnlyPrice, flight.combinedOfferCurrency || 'AUD');
                      const baseTotalWithSurcharge = getCombinedOfferPriceAmount(actualOutboundPrice + returnOnlyPrice, flight.combinedOfferCurrency || 'AUD');
                      
                      // Display return price with 1.05 applied
                      displayPrice = formatCombinedOfferPrice(returnOnlyPrice, flight.combinedOfferCurrency || 'AUD');
                      displayPriceAmount = returnWithSurcharge;
                      priceLabel = 'Return flight';
                      showPlusSign = false;
                      
                      // Store data for booking - use SELECTED outbound, not bundled price
                      flight.combinedOfferData = {
                        combinedOfferId: flight.combinedOfferId,
                        combinedOfferPrice: flight.combinedOfferPrice,
                        combinedOfferCurrency: flight.combinedOfferCurrency || 'AUD',
                        outboundPrice: actualOutboundPrice, // Selected outbound price
                        returnPrice: returnOnlyPrice,
                        combinedSavings: flight.combinedSavings || 0,
                      };
                    } else {
                      // For outbound or one-way flights, show normal price
                      displayPrice = formatDisplayPrice(flight.price, flight.priceBreakdown?.currency || 'AUD');
                      displayPriceAmount = getDisplayPriceAmount(flight.price, flight.priceBreakdown?.currency || 'AUD');
                      priceLabel = 'From';
                      showPlusSign = false;
                    }
                    
                    return (
                      <div className="flight-result-reveal" key={flight.code} style={{ '--result-index': index }}>
                        <FlightResult
                          flight={{
                            from: flight.from,
                            to: flight.to,
                            date: flight.date,
                            class: flight.class,
                            id: flight.code,
                            airline: flight.airline || 'AIRFLYNOW',
                            code: flight.code,
                            departure: flight.departureTime,
                            departureDate: departureDateLabel,
                            arrival: flight.arrivalTime,
                            arrivalDate: arrivalDateLabel,
                            duration: flight.duration || '~',
                            stops: `${flight.class}`,
                            price: flight.price,
                            priceCurrency: flight.priceBreakdown?.currency || 'AUD',
                            displayPrice: displayPrice,
                            displayPriceAmount: displayPriceAmount,
                            displayCurrency: selectedCurrency,
                            priceLabel: priceLabel,
                            showPlusSign: showPlusSign,
                            type: flight.type,
                            source: flight.source || 'Local',
                            stopCities: (flight.stopCities || []).map((stop) => getStopLabel(stop)),
                            stopDurations: flight.stopDurations || [],
                            segments: flight.segments || [],
                            priceBreakdown: flight.priceBreakdown || null,
                            arrivalDateRaw: flight.arrivalDate || null,
                            fareConditions: flight.fareConditions || null,
                            logoUrl: flight.logoUrl || null,
                            // NEW: Combined offer data
                            combinedOfferData: flight.combinedOfferData || null,
                            isCombinedOffer: isReturnWithCombined,
                            // Calculate total: (SELECTED_outbound + return) × 1.05
                            // NOTE: $20 booking fee added on booking details page, not here
                            combinedTotalPrice: isReturnWithCombined 
                              ? formatCombinedOfferPrice(
                                  (selectedOutboundFlight?.price || flight.outboundPrice || 0) + (flight.returnOnlyPrice || 0), 
                                  flight.combinedOfferCurrency || 'AUD'
                                )
                              : null,
                          }}
                          badge={badge}
                          fareOptionCount={fareOptionCount}
                          onSelect={() => {
                            // Always show fare options modal (even for 1 fare) for transparency
                            if (!rescheduleContext) {
                              setFarePickerGroup(group);
                            } else {
                              handleSelectFlight(flight);
                            }
                          }}
                          selected={isSelectingReturn && selectedReturnFlight?.code === flight.code}
                        />
                      </div>
                    );
                  };

                  return (
                    <>
                      {suggestedGroups.length > 0 && (
                        <>
                          <div className="air-picks-divider">
                            <span className="air-picks-divider__icon" aria-hidden="true">✈️</span>
                            <span className="air-picks-divider__label">AIR Suggested</span>
                            <span className="air-picks-divider__sub">Our top picks for this route</span>
                          </div>
                          {suggestedGroups.map(({ grp, badge }, i) => renderFlightCard(grp, i, badge))}
                          {restGroups.length > 0 && (
                            <div className="air-picks-divider air-picks-divider--rest">
                              <span className="air-picks-divider__label">All Flights</span>
                              <span className="air-picks-divider__sub">{restGroups.length} more result{restGroups.length !== 1 ? 's' : ''}</span>
                            </div>
                          )}
                        </>
                      )}
                      {restGroups.map((grp, i) => renderFlightCard(grp, suggestedGroups.length + i, null))}
                    </>
                  );
                })()
              : (
                returnError && isSelectingReturn ? (
                  <div className="search-no-flights" role="alert">
                    <div className="search-no-flights__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
                    </div>
                    <h3 className="search-no-flights__title">Return search failed</h3>
                    <p className="search-no-flights__body">{returnError}</p>
                    <div className="search-no-flights__actions">
                      <button
                        type="button"
                        className="search-no-flights__btn search-no-flights__btn--primary"
                        onClick={handleRetryReturnSearch}
                      >
                        Try again
                      </button>
                      <button
                        type="button"
                        className="search-no-flights__btn search-no-flights__btn--secondary"
                        onClick={() => { setSelectedOutboundFlight(null); setReturnError(''); setReturnFlights([]); }}
                      >
                        Change outbound flight
                      </button>
                    </div>
                    <p className="search-no-flights__hint">
                      Return searches can time out on busy international routes. Tap &ldquo;Try again&rdquo; — it usually works on the second attempt.
                    </p>
                  </div>
                ) : error ? (
                  /* Search failed — service error or no inventory on this route */
                  <div className="search-no-flights" role="alert">
                    <div className="search-no-flights__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="currentColor"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
                    </div>
                    <h3 className="search-no-flights__title">Route Not Available Yet</h3>
                    <p className="search-no-flights__body">
                      We're working to bring flights from <strong>{formData.from}</strong> to{' '}
                      <strong>{formData.to}</strong> into our system. This route isn't available yet,
                      but we're constantly adding new routes and airlines.
                    </p>
                    <div className="search-no-flights__actions">
                      <button
                        type="button"
                        className="search-no-flights__btn search-no-flights__btn--primary"
                        onClick={() => runSearchWithFormData(formData, { directOnly: directOnlySearch })}
                      >
                        Try again
                      </button>
                      <button
                        type="button"
                        className="search-no-flights__btn search-no-flights__btn--secondary"
                        onClick={handleSwap}
                      >
                        Swap airports
                      </button>
                    </div>
                    <p className="search-no-flights__hint">
                      Tip: Try nearby airports or check back soon as we're expanding our coverage daily.
                    </p>
                  </div>
                ) : visibleActiveFlights.length === 0 ? (
                  /* API returned 0 flights with no error */
                  <div className="search-no-flights search-no-flights--empty" role="status">
                    <div className="search-no-flights__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    </div>
                    <h3 className="search-no-flights__title">Coming Soon</h3>
                    <p className="search-no-flights__body">
                      We're working to bring flights from <strong>{formData.from}</strong> to{' '}
                      <strong>{formData.to}</strong> into our system. Check back soon or try a different route!
                    </p>
                    <div className="search-no-flights__actions">
                      <button
                        type="button"
                        className="search-no-flights__btn search-no-flights__btn--secondary"
                        onClick={handleSwap}
                      >
                        Swap airports
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Results exist but active filters hide all of them */
                  <div className="no-results-filters" role="status">
                    <div className="no-results-filters__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                    </div>
                    <p className="no-results-filters__title">No flights match your filters</p>
                    <p className="no-results-filters__sub">{visibleActiveFlights.length} flight{visibleActiveFlights.length !== 1 ? 's' : ''} found — try removing a filter to see results.</p>
                    <button
                      type="button"
                      className="no-results-filters__clear"
                      onClick={() => setFilters((prev) => ({
                        ...prev,
                        airlines: [],
                        stops: [],
                        departureWindow: 'any',
                        arrivalWindow: 'any',
                        maxPrice: priceSliderMax,
                      }))}
                    >
                      Clear all filters
                    </button>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Fare picker modal — shown when a multi-offer flight card is clicked */}
      {farePickerGroup && (
        <FarePickerModal
          flights={farePickerGroup}
          isRoundTrip={isRoundTrip}
          isSelectingReturn={isSelectingReturn}
          onSelect={(pickedFlight) => {
            setFarePickerGroup(null);
            handleSelectFlight(pickedFlight);
          }}
          onClose={() => setFarePickerGroup(null)}
          formatPrice={(flight) => {
            // For return flights with combined offers, calculate using SELECTED outbound + return
            if (isSelectingReturn && flight.returnOnlyPrice != null && selectedOutboundFlight?.price != null) {
              // Use selected outbound price + this return price (both × 1.05)
              const totalBase = selectedOutboundFlight.price + flight.returnOnlyPrice;
              const amount = getCombinedOfferPriceAmount(totalBase, flight.combinedOfferCurrency || 'AUD');
              const currency = selectedCurrency;
              const { currency: sym, amount: amt } = formatCurrencyParts ? formatCurrencyParts(amount, currency) : { currency, amount: amount.toFixed(2) };
              return { currency: sym, amount: amt };
            }
            // For outbound or non-combined flights, use regular pricing
            const amount = getDisplayPriceAmount(flight.price, flight.priceBreakdown?.currency || 'AUD');
            const currency = selectedCurrency;
            // reuse formatCurrencyParts for consistent display
            const { currency: sym, amount: amt } = formatCurrencyParts ? formatCurrencyParts(amount, currency) : { currency, amount: amount.toFixed(2) };
            return { currency: sym, amount: amt };
          }}
          routeLabel={`${farePickerGroup[0]?.from} → ${farePickerGroup[0]?.to}`}
          airlineName={farePickerGroup[0]?.airline || 'AIRFLYNOW'}
          dateLabel={farePickerGroup[0]?.date
            ? new Date(`${farePickerGroup[0].date}T00:00:00`).toLocaleDateString('en-AU', {
                weekday: 'short', day: 'numeric', month: 'short', year: '2-digit',
              })
            : ''}
        />
      )}

      {/* Sticky Round-Trip Selection Bar */}
      {isSelectingReturn && showRoundTripBar && (
        <div className="rt-bar">
          {/* Outbound leg */}
          <div className="rt-bar__leg">
            <div className="rt-bar__airline">{selectedOutboundFlight.airline || 'AIRFLYNOW'}</div>
            <div className="rt-bar__leg-body">
              <div className="rt-bar__route">
                <span className="rt-bar__iata">{selectedOutboundFlight.from}</span>
                <span className="rt-bar__arrow">&#8594;</span>
                <span className="rt-bar__iata">{selectedOutboundFlight.iata || selectedOutboundFlight.to}</span>
              </div>
              <div className="rt-bar__date">{new Date(selectedOutboundFlight.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
              <div className="rt-bar__times">{selectedOutboundFlight.departureTime} &rarr; {selectedOutboundFlight.arrivalTime}</div>
              <div className="rt-bar__meta">{selectedOutboundFlight.duration || '~'} &nbsp;&middot;&nbsp; {selectedOutboundFlight.type}</div>
            </div>
          </div>

          <div className="rt-bar__divider" />

          {/* Return leg */}
          {selectedReturnFlight ? (
            <div className="rt-bar__leg rt-bar__leg--return">
              <div className="rt-bar__airline">{selectedReturnFlight.airline || 'AIRFLYNOW'}</div>
              <div className="rt-bar__leg-body">
                <div className="rt-bar__route">
                  <span className="rt-bar__iata">{selectedReturnFlight.from}</span>
                  <span className="rt-bar__arrow">&#8594;</span>
                  <span className="rt-bar__iata">{selectedReturnFlight.to}</span>
                </div>
                <div className="rt-bar__date">{new Date(selectedReturnFlight.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                <div className="rt-bar__times">{selectedReturnFlight.departureTime} &rarr; {selectedReturnFlight.arrivalTime}</div>
                <div className="rt-bar__meta">{selectedReturnFlight.duration || '~'} &nbsp;&middot;&nbsp; {selectedReturnFlight.type}</div>
              </div>
            </div>
          ) : (
            <div className="rt-bar__leg rt-bar__leg--empty">
              <span className="rt-bar__empty-icon">&#8617;</span>
              <span className="rt-bar__empty-label">Select a return flight</span>
            </div>
          )}

          <div className="rt-bar__divider rt-bar__divider--before-fare" />

          {/* Fare + actions */}
          <div className="rt-bar__fare">
            <div className="rt-bar__fare-label">Total Fare</div>
            <div className="rt-bar__fare-amount">
              {(() => {
                // SINGLE SOURCE OF TRUTH: Always use selected outbound + selected return
                // Formula: (selectedOutbound × 1.05) + (selectedReturn × 1.05)
                // NOTE: $20 booking fee added on booking details page
                if (selectedOutboundFlight?.price != null && selectedReturnFlight?.returnOnlyPrice != null) {
                  const totalBase = selectedOutboundFlight.price + selectedReturnFlight.returnOnlyPrice;
                  const totalWithSurcharge = formatCombinedOfferPrice(
                    totalBase,
                    selectedReturnFlight.combinedOfferCurrency || 'AUD'
                  );
                  
                  return totalWithSurcharge;
                }
                // Fallback for simple addition
                return formatDisplayPrice(
                  (selectedOutboundFlight?.price || 0) + (selectedReturnFlight?.price || 0),
                  selectedOutboundFlight?.priceBreakdown?.currency || 'AUD',
                );
              })()}
            </div>
            <div className="rt-bar__fare-pax">per person &times; {totalTravellers}</div>
          </div>

          <div className="rt-bar__actions">
            <button
              type="button"
              className="rt-bar__change-btn"
              onClick={() => {
                setSelectedOutboundFlight(null);
                setSelectedReturnFlight(null);
                setReturnFlights([]);
                setError('');
              }}
            >
              Change Outbound
            </button>
            <button
              type="button"
              className="rt-bar__confirm-btn"
              disabled={!selectedReturnFlight}
              onClick={handleViewFares}
            >
              {selectedReturnFlight ? 'Proceed →' : 'Pick Return First'}
            </button>
          </div>
        </div>
      )}

      {/* Stale Search Results Modal */}
      {staleSearchModal && (() => {
        const ageMinutes = searchTimestamp ? Math.floor((Date.now() - searchTimestamp) / 60000) : 0;
        
        // Don't show modal if results are fresh (less than 10 minutes old)
        if (ageMinutes < 10) {
          // Results are fresh - close the modal silently
          setStaleSearchModal(false);
          return null;
        }
        
        return (
          <div className="offer-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="stale-search-title">
            <div className="offer-expired-modal" style={{ maxWidth: 520 }}>
              <div className="offer-expired-icon" aria-hidden="true" style={{ color: '#f59e0b' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h2 id="stale-search-title" style={{ fontSize: 24, marginBottom: 16, color: '#0f172a' }}>
                Search Results Are {ageMinutes} Minutes Old
              </h2>
              <p style={{ fontSize: 15, lineHeight: 1.7, marginBottom: 16, color: '#64748b' }}>
                Flight prices and availability change frequently. These results may no longer reflect the current fares.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.7, marginBottom: 24, color: '#64748b' }}>
                We recommend refreshing your search to see the latest available flights and pricing.
              </p>
              <div className="offer-expired-actions">
                <button
                  type="button"
                  className="offer-expired-btn offer-expired-btn--primary"
                  onClick={() => {
                    setStaleSearchModal(false);
                    runSearchWithFormData(formData, { directOnly: directOnlySearch });
                  }}
                >
                  🔄 Refresh Search
                </button>
                <button
                  type="button"
                  className="offer-expired-btn offer-expired-btn--secondary"
                  onClick={() => {
                    setStaleSearchModal(false);
                    navigate('/');
                  }}
                  style={{ marginLeft: '8px' }}
                >
                  🏠 Go to Home
                </button>
                <button
                  type="button"
                  className="offer-expired-btn offer-expired-btn--ghost"
                  onClick={() => setStaleSearchModal(false)}
                >
                  Continue Anyway
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default SearchFlights;

